import React, { useState, useEffect } from "react";
import { users as usersApi, profiles as profilesApi, auth as authApi, ref as refApi, construction as constructionApi } from "../services/api"; // v3.4.220 — constructionApi للمهندس المسؤول
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import PortalPermissionsPanel from "./PortalPermissionsPanel"; // v3.4.454 — صلاحيات المنظم التسويقي
import PmPermissionsPanel from "./PmPermissionsPanel"; // v3.4.466 — صلاحيات إدارة المشاريع
import { FiPlus, FiTrash2, FiCheck, FiX, FiMail, FiEye, FiUsers, FiSettings, FiUserPlus, FiRefreshCw, FiPlay, FiSquare, FiDownload, FiUpload } from "react-icons/fi";

const VIEW_SCOPES = [
  { value: 'all', label: 'الكل', color: '#2563eb' },
];

const VISIBILITY_LABELS = [
  { key: "can_view_reservations", label: "صفحة الحجوزات" },
  { key: "can_view_clients", label: "صفحة العملاء" },
  { key: "can_view_client_groups", label: "مجموعات العملاء" },
  { key: "can_view_developers", label: "صفحة المطورين" },
  { key: "can_view_marketers", label: "صفحة المسوقين" },
  { key: "can_view_marketer_groups", label: "مجموعات المسوقين" },
  { key: "can_view_projects", label: "صفحة المشاريع" },
  { key: "can_view_prospects", label: "المحتملون ونظام التسويق (عملاء/أراضٍ/عروض مشاريع)" },
  { key: "can_view_prospect_groups", label: "مجموعات المحتملين" },
  { key: "can_view_contracts", label: "صفحة العقود" },
  { key: "can_view_client_comm", label: "تواصل العملاء" },
  { key: "can_view_marketer_comm", label: "تواصل المسوقين" },
  { key: "can_view_business_comm", label: "التواصل المؤسسي" },
  // v3.3.308: العمليات التشغيلية
  { key: "can_view_reservations_scheduler", label: "تنسيق الحجوزات" },
  { key: "can_view_sales", label: "المبيعات" }, // v3.4.538
  { key: "can_view_payments", label: "المدفوعات المحصّلة" },
  { key: "can_view_collections", label: "إدارة الدفعات والتحصيلات" },
  // v3.3.308: التحليل المالي — v3.3.431 completed
  { key: "can_view_analytics", label: "الإحصائيات" },
  { key: "can_view_financial_reports", label: "التقارير المالية" },
  { key: "can_view_commissions", label: "الدلالات والعمولات" },
  { key: "can_view_cash_flow", label: "تفاصيل الإيرادات" },
  { key: "can_view_cash_flow_statement", label: "التدفقات النقدية" },
  { key: "can_view_benchmarking", label: "مقارنة المشاريع" },
  { key: "can_view_company_assets", label: "أصول الشركة" },
  { key: "can_view_capex", label: "المصاريف الرأسمالية" },
  // v3.3.576 — Treasury (الخزينة المالية)
  { key: "can_view_treasury", label: "الخزينة المالية" },
  // v3.3.864 — أقسام كانت ناقصة من قائمة العرض والإظهار
  { key: "can_view_office_visitors", label: "سجل زوار المكتب" },
  { key: "can_view_company_whatsapp", label: "واتس الرقم الرئيسي للشركة" }, // v3.4.180
  { key: "can_view_daleelak_whatsapp", label: "واتس رقم دليلك الرسمي" }, // v3.4.587
  { key: "can_view_marketing_analytics", label: "تحليل التسويق" },
  { key: "can_view_admin_fees", label: "الرسوم الإدارية للمشاريع" },
  { key: "can_view_sukuk", label: "التمويل (الصكوك)" },
  { key: "can_view_accounting", label: "البرنامج المحاسبي" },
  { key: "can_view_construction", label: "المشاريع الإنشائية" },
  // النظام
  { key: "can_view_settings", label: "الإعدادات" },
  { key: "can_view_audit", label: "التنبيهات (Audit)" },
];

// ═══════ v3.4.022 — مجموعات القائمة الجانبية: البروفايل يُمنح مجموعات، وتظهر أقسام الصلاحيات للمجموعات الممنوحة فقط ═══════
const SIDEBAR_GROUPS = [ // v3.4.348 — تطابق مجموعات القائمة الجانبية (App.js NavGroup) واحدةً واحدة — v3.4.496: وبنفس ترتيبها
  { key: "chairman", label: "👔 Chairman Office", views: ["can_view_chairman_office"] }, // الإيميلات، التقويم، التواصل الخارجي، Chairman Contacts
  { key: "alma", label: "🏛️ Alma Capital", views: ["can_view_alma", "alma_can_view_content", "alma_can_view_messages", "alma_can_view_compliance", "alma_can_view_people", "alma_can_view_startup", "alma_can_view_settings"] }, // v3.4.835 — مجموعة مستقلة + صلاحية لكل تبويب
  { key: "office", label: "🏢 إدارة المكتب", views: ["can_view_office_visitors", "can_view_company_whatsapp", "can_view_daleelak_whatsapp", "can_view_tasks", "can_view_letters"] }, // v3.4.180
  { key: "reservations", label: "📋 الحجوزات", views: ["can_view_reservations"] },
  { key: "projects", label: "🏘️ المشاريع", views: ["can_view_projects", "can_view_marketing_offers"] }, // كانت ضمن الحجوزات
  { key: "media", label: "🎨 سوشل ميديا وجرافيك", views: ["can_view_media_library", "can_view_social_media", "can_view_websites", "can_view_digital_accounts"] }, /* v3.4.796 websites */ /* v3.4.389 */ /* v3.4.387 */ // v3.4.361
  { key: "vouchers", label: "🧾 سندات القبض والصرف", views: ["can_view_vouchers"] }, // v3.4.611
  { key: "ops", label: "⚙️ العمليات التشغيلية", views: ["can_view_reservations_scheduler", "can_view_sales", "can_view_quotations", "can_view_payments", "can_view_collections", "can_view_marketing_analytics", "can_view_contracts", "can_view_disbursements"] }, // العقود وصرف الدلالات انتقلا هنا (v3.4.249)
  { key: "hr", label: "👥 الموارد البشرية", views: ["can_view_hr"] }, // v3.4.192
  { key: "legal", label: "⚖️ الإدارة القانونية", views: ["can_view_legal"] }, // v3.4.420
  { key: "comm", label: "📞 التواصل الخارجي", views: ["can_view_client_comm", "can_view_marketer_comm", "can_view_business_comm", "can_view_wa_updates_group", "can_view_wa_admin", "wa_session_can_manage", "can_view_invitations"] }, /* v3.4.410 · v3.4.744 إدارة الواتس · v3.4.747 جلسات واتس */
  { key: "data", label: "🗂 قواعد البيانات", views: ["can_view_clients", "can_view_client_groups", "can_view_developers", "can_view_marketers", "can_view_marketer_groups", "can_view_prospects", "can_view_prospect_groups"] }, // v3.4.186
  { key: "marketing", label: "📣 نظام التسويق", views: ["can_view_client_prospects", "can_view_callcenter", "can_view_portal_app", "can_view_land_offers", "can_view_aqar_listings", /* v3.4.399 */ "can_view_project_offers", "can_view_business_chat"] }, // v3.4.107
  { key: "construction", label: "🏗 إدارة المشاريع", views: ["can_view_construction", "can_view_weekly_orders", "can_view_subcontracts", "can_view_supply_contracts", "can_view_contract_claims", "can_view_receipt_requests", "can_view_drawings", "can_view_contract_structure"] },
  { key: "screen", label: "🖥️ الشاشة التفاعلية", views: ["can_view_screen"] }, // v3.4.492 — مجموعة مستقلة في القائمة الجانبية
  { key: "finance", label: "📊 التحليل المالي", views: ["can_view_analytics", "can_view_capex", "can_view_company_assets", "can_view_financial_reports", "can_view_commissions", "can_view_admin_fees", "can_view_cash_flow", "can_view_cash_flow_statement", "can_view_benchmarking", "can_view_treasury", "can_view_payroll" /* v3.4.837 */, "can_view_sukuk", "can_view_accounting"] },
  { key: "system", label: "🛠 النظام", views: ["can_view_settings", "can_view_audit", "can_view_cloud_billing"] },
];
const ALL_GROUP_VIEW_KEYS = SIDEBAR_GROUPS.flatMap(g => g.views);
// المجموعة "ممنوحة" إذا كانت أي صفحة من صفحاتها مفعّلة
const groupOn = (perms, g) => g.views.some(k => perms && perms[k]);

// v3.4.022 — محدد المجموعات: تفعيل مجموعة يفعّل كل صفحاتها (وتُضبط الصفحات تفصيليًا من أقسام الصلاحيات)
function GroupsSelector({ value, onChange, hint }) {
  return (
    <div style={{ marginBottom: 16, background: "#fefce8", padding: 12, borderRadius: 8, border: "1px solid #fde68a" }}>
      <label style={{ fontSize: 11, color: "#854d0e", fontWeight: 700, display: "block", marginBottom: 8 }}>
        المجموعات المسموح الاطلاع عليها (من القائمة الجانبية)
        {hint && <span style={{ fontWeight: 500, color: "#a16207" }}> — {hint}</span>}
      </label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {SIDEBAR_GROUPS.map(g => {
          const on = groupOn(value, g);
          return (
            <label key={g.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", padding: "6px 12px", borderRadius: 14, fontWeight: on ? 800 : 600, border: on ? "2px solid #0f766e" : "1px solid #e2e8f0", background: on ? "#f0fdfa" : "#fff", color: on ? "#0f766e" : "#64748b" }}>
              <input type="checkbox" checked={on}
                onChange={e => onChange(Object.fromEntries(g.views.map(k => [k, e.target.checked])))}
                style={{ width: 14, height: 14 }} />
              {g.label}
            </label>
          );
        })}
      </div>
    </div>
  );
}

// v3.4.024 — صندوق مجموعة: إطار كامل حول أقسام صلاحيات المجموعة الواحدة — يختفي كليًا عند عدم منح المجموعة
function GroupBox({ gkey, profile, children }) { // v3.4.348 — gkey قد يحوي عدة مجموعات مفصولة بـ | (يظهر إن مُنحت أي منها)
  const gs = String(gkey).split("|").map(k => SIDEBAR_GROUPS.find(x => x.key === k)).filter(Boolean);
  if (!gs.length || !gs.some(g => groupOn(profile, g))) return null;
  return (
    <div style={{ border: "2px solid #99f6e4", borderRadius: 12, padding: 12, marginBottom: 16 }}>
      <div style={{ fontSize: 13.5, fontWeight: 900, color: "#0f766e", marginBottom: 10, borderBottom: "1px dashed #99f6e4", paddingBottom: 8 }}>{gs.filter(g => groupOn(profile, g)).map(g => g.label).join(" + ")}</div>
      {children}
    </div>
  );
}

// ─── Module Permissions Section (reusable) ───────────────────────────────────
// ═══════ AVAILABLE COLUMNS PER MODULE ═══════
const RES_COLUMNS = [
  { key: "serial_number", label: "م" },
  { key: "id", label: "#" },
  { key: "date", label: "التاريخ" },
  { key: "operation_type", label: "نوع العملية" },
  { key: "client_name", label: "العميل" },
  { key: "client_type", label: "نوع العميل" },
  { key: "developer", label: "المطور" },
  { key: "unit_type", label: "الوحدة" },
  { key: "readiness", label: "الجاهزية" },
  { key: "purchase_purpose", label: "هدف الشراء" },
  { key: "value", label: "القيمة" },
  { key: "deposit_amount", label: "العربون" },
  { key: "deposit_status", label: "حالة العربون" },
  { key: "bank_account", label: "الحساب" },
  { key: "dalala_amount", label: "الدلالة" },
  { key: "dalala_status", label: "حالة صرف الدلالة" },
  { key: "arboun_status", label: "حالة إرجاع العربون" },
  { key: "operation_status", label: "حالة العملية" },
  { key: "marketer", label: "المسوق" },
  { key: "notes", label: "ملاحظات" },
  { key: "ext_marketer_share", label: "خارجي" },
  { key: "int_marketer_share", label: "داخلي" },
  { key: "waha_share", label: "الواحة" },
  { key: "sales_mgr_share", label: "م.مبيعات" },
  { key: "mkt_mgr_share", label: "م.مسوقات" },
  { key: "company_share", label: "الشركة" },
  { key: "commission_status_res", label: "حالة(إدارة)" },
  { key: "commission_status_acc", label: "حالة(محاسبة)" },
  { key: "notes_extra", label: "ملاحظات٢" },
];

const CLIENTS_COLUMNS = [
  { key: "name", label: "اسم العميل" },
  { key: "client_number", label: "رقم العميل" },
  { key: "client_type", label: "مصدر العميل" },
  { key: "classification", label: "التصنيف" },
  { key: "phone", label: "الهاتف" },
  { key: "notes", label: "ملاحظات" },
  { key: "created_at", label: "تاريخ الإضافة" },
];

const DEVS_COLUMNS = [
  { key: "name", label: "اسم المطور" },
  { key: "representative_name", label: "ممثل المطور" },
  { key: "representative_phone", label: "رقم الممثل" },
  { key: "notes", label: "ملاحظات" },
  { key: "created_at", label: "تاريخ الإضافة" },
];

const MKTS_COLUMNS = [
  { key: "name", label: "اسم المسوق" },
  { key: "phone", label: "الهاتف" },
  { key: "office", label: "شركة المسوق" },
  { key: "interests", label: "اهتمامات" },
  { key: "notes", label: "ملاحظات" },
  { key: "created_at", label: "تاريخ الإضافة" },
];

const PROJECTS_COLUMNS = [
  { key: "name", label: "اسم المشروع" },
  { key: "developer", label: "المطور" },
  { key: "location", label: "الموقع" },
  { key: "total_units", label: "عدد الوحدات" },
  { key: "status", label: "الحالة" },
  { key: "notes", label: "ملاحظات" },
  { key: "building", label: "المبنى" },
  { key: "floor", label: "الدور" },
  { key: "unit_code", label: "رمز الوحدة" },
  { key: "unit_number", label: "رقم الوحد" },
  { key: "rooms", label: "عدد الغرف" },
  { key: "bathrooms", label: "عدد الحمام" },
  { key: "kitchen", label: "المطبخ" },
  { key: "living", label: "الصالة" },
  { key: "area", label: "المساحة" },
  { key: "price", label: "السعر الرسمي" },
  { key: "unit_status", label: "حالة الوحدة" },
  { key: "facade", label: "الواجهة" },
];

// ═══════ Columns Checkbox Grid ═══════
function ColumnsCheckboxGrid({ fieldKey, columns, color, profile, setProfile }) {
  // Parse current value: empty string = all visible
  const currentVal = (profile[fieldKey] || "").trim();
  const selectedKeys = currentVal ? currentVal.split(",").map(s => s.trim()).filter(Boolean) : [];
  const allSelected = selectedKeys.length === 0; // empty = all

  const isChecked = (colKey) => allSelected || selectedKeys.includes(colKey);

  const toggleColumn = (colKey) => {
    if (allSelected) {
      // Switching from "all" → uncheck this one column (select all others)
      const newKeys = columns.filter(c => c.key !== colKey).map(c => c.key);
      setProfile(p => ({ ...p, [fieldKey]: newKeys.join(",") }));
    } else {
      if (selectedKeys.includes(colKey)) {
        // Uncheck: remove it
        const newKeys = selectedKeys.filter(k => k !== colKey);
        // If none remain, set empty = all (prevent empty selection)
        setProfile(p => ({ ...p, [fieldKey]: newKeys.length === 0 ? "" : newKeys.join(",") }));
      } else {
        // Check: add it
        const newKeys = [...selectedKeys, colKey];
        // If all are now selected, reset to empty = all
        if (newKeys.length >= columns.length) {
          setProfile(p => ({ ...p, [fieldKey]: "" }));
        } else {
          setProfile(p => ({ ...p, [fieldKey]: newKeys.join(",") }));
        }
      }
    }
  };

  const toggleAll = () => {
    if (allSelected) {
      // Uncheck all → set to empty string (but we don't want zero columns, so keep all)
      // Actually: toggle all OFF is not useful, so we just clear = all
      setProfile(p => ({ ...p, [fieldKey]: "" }));
    } else {
      // Select all → empty string
      setProfile(p => ({ ...p, [fieldKey]: "" }));
    }
  };

  const checkedCount = allSelected ? columns.length : selectedKeys.length;

  return (
    <div key={fieldKey} style={{ width: "100%", marginTop: 6 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontWeight: 600, color: "#64748b", fontSize: 10 }}>
          أعمدة ظاهرة ({checkedCount}/{columns.length})
        </span>
        <button onClick={toggleAll} style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: 9, color: color, fontWeight: 600, fontFamily: "inherit",
          textDecoration: "underline",
        }}>
          {allSelected ? "إلغاء تحديد الكل" : "تحديد الكل"}
        </button>
      </div>
      <div style={{
        display: "flex", gap: 4, flexWrap: "wrap",
        padding: 8, background: "#fff", borderRadius: 6, border: "1px solid #e2e8f0",
      }}>
        {columns.map(col => {
          const checked = isChecked(col.key);
          return (
            <label key={col.key} style={{
              display: "flex", alignItems: "center", gap: 3, padding: "2px 8px",
              background: checked ? color + "12" : "#f8fafc",
              border: `1px solid ${checked ? color + "40" : "#e2e8f0"}`,
              borderRadius: 12, cursor: "pointer", fontSize: 10, fontWeight: 500,
              color: checked ? color : "#94a3b8",
              transition: "all 0.15s",
              userSelect: "none",
            }}>
              <input type="checkbox" checked={checked}
                onChange={() => toggleColumn(col.key)}
                style={{ width: 11, height: 11, accentColor: color }} />
              {col.label}
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ═══════ v3.4.712 — مفاتيح صلاحيات موحّدة (بطلب المالك: «عرض» و«تعديل» متجاورتان لكل قسم فرعي بشكل احترافي) ═══════
// كل صلاحية = مفتاح تبديل بأيقونة حسب نوع الإجراء، والترتيب ثابت داخل القسم: عرض ← تعديل ← إضافة ← حذف ← إرسال ← تصدير ← استيراد ← أخرى
const PERM_KINDS = [
  ["view", /^(عرض|مشاهدة|استعراض)/, "👁"],
  ["edit", /^(تعديل|تغيير|إدارة|تعبئة|اعتماد|تنفيذ|الرد|تحرير)|\/\s*تعديل/, "✏️"],
  ["add", /^(إضافة|إنشاء|رفع|ربط|طلب|تسجيل)/, "➕"],
  ["delete", /^(حذف|إلغاء)/, "🗑️"],
  ["send", /^(إرسال|مشاركة|نشر)/, "📨"],
  ["export", /^(تصدير|تحميل|طباعة)/, "📤"],
  ["import", /^(استيراد|جلب)/, "📥"],
];
const KIND_RANK = { view: 0, edit: 1, add: 2, delete: 3, send: 4, export: 5, import: 6, other: 7 };
const cleanLabel = (label) => String(label || "").replace(/^[^\p{L}\p{N}(]+/u, "").trim(); // بلا الإيموجي في أول النص (الأيقونة تُضاف هنا)
const permKind = (label, key) => {
  const l = cleanLabel(label);
  if (/^عرض/.test(l) || (!l && /can_view_/.test(String(key || "")))) return { k: "view", icon: "👁" };
  for (const [k, re, icon] of PERM_KINDS) if (re.test(l)) return { k, icon };
  if (/can_view_/.test(String(key || ""))) return { k: "view", icon: "👁" };
  return { k: "other", icon: "▫️" };
};
const sortPerms = (fields, masterKey) => fields.map((f, i) => ({ f, i })).sort((a, b) => {
  if (a.f.key === masterKey) return -1; if (b.f.key === masterKey) return 1;
  const ab = a.f.type === "bool", bb = b.f.type === "bool";
  if (ab !== bb) return ab ? -1 : 1;
  if (!ab) return a.i - b.i;
  return (KIND_RANK[permKind(a.f.label, a.f.key).k] - KIND_RANK[permKind(b.f.label, b.f.key).k]) || a.i - b.i;
}).map(x => x.f);
function PermToggle({ on, onChange, label, color, disabled, master }) {
  const { icon } = permKind(label);
  const text = cleanLabel(label) || label;
  return (
    <button type="button" title={text} disabled={!!disabled} onClick={() => onChange(!on)}
      style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 10px 5px 8px", borderRadius: 10, cursor: disabled ? "default" : "pointer", fontFamily: "inherit", textAlign: "start",
        fontSize: 11, fontWeight: on ? 700 : 500, color: on ? "#0f172a" : "#64748b", background: on ? "#fff" : "#f8fafc", border: `1px solid ${on ? color + "66" : "#e2e8f0"}`,
        boxShadow: on ? `inset 3px 0 0 ${color}` : "none", opacity: disabled ? 0.5 : 1, maxWidth: 380, minWidth: 150, transition: "all .15s" }}>
      <span aria-hidden="true" style={{ position: "relative", display: "inline-block", width: 28, height: 15, background: on ? color : "#cbd5e1", borderRadius: 8, flex: "none", transition: "background .2s" }}>
        <span style={{ position: "absolute", top: 2, [on ? "right" : "left"]: 2, width: 11, height: 11, background: "#fff", borderRadius: "50%", boxShadow: "0 1px 2px rgba(0,0,0,.25)", transition: "all .2s" }} />
      </span>
      <span style={{ fontSize: 12, flex: "none" }}>{icon}</span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{text}</span>
      {master && <span style={{ fontSize: 9, color, fontWeight: 800, flex: "none" }}>رئيسي</span>}
    </button>
  );
}
function ModulePermsSection({ title, color, profile, setProfile, fields, mainKey, footer }) {
  // v3.4.022 — يظهر القسم فقط إذا كانت مجموعته (من القائمة الجانبية) ممنوحة للبروفايل
  const grp = SIDEBAR_GROUPS.find(g => g.views.includes(mainKey));
  if (grp && !groupOn(profile, grp)) return null;
  const DAYS_OPTIONS = [
    { value: 0, label: "ممنوع" },
    { value: 7, label: "7 أيام" },
    { value: 14, label: "14 يوم" },
    { value: 30, label: "30 يوم" },
    { value: 60, label: "60 يوم" }, // v3.4.242
    { value: 90, label: "90 يوم" },
    { value: 120, label: "120 يوم" },
    { value: -1, label: "غير محدود" },
  ];

  // Separate columns fields from regular fields
  const regularFields = fields.filter(f => f.type !== "columns");
  const columnsFields = fields.filter(f => f.type === "columns");

  // v3.3.309: Section master toggle (hybrid behavior)
  // If mainKey provided, that field controls visibility.
  // Disable: turns OFF all bool fields in section.
  // Enable: only turns ON the main key (admin chooses sub-perms manually).
  const masterKey = mainKey || (regularFields[0]?.type === "bool" ? regularFields[0].key : null);
  const sectionEnabled = masterKey ? (profile[masterKey] !== false ? !!profile[masterKey] : false) : true;

  const toggleSection = (enabled) => {
    if (!masterKey) return;
    setProfile(p => {
      const next = { ...p };
      if (enabled) {
        // Hybrid: only turn on the master key, leave rest as-is for admin
        next[masterKey] = true;
      } else {
        // Disable: turn off ALL bool fields in this section
        for (const f of regularFields) {
          if (f.type === "bool") next[f.key] = false;
        }
      }
      return next;
    });
  };

  const showFields = sectionEnabled || !masterKey;

  return (
    <div style={{ marginBottom: 12, padding: 12, background: sectionEnabled ? color + "08" : "#f8fafc", borderRadius: 8, border: `1px solid ${sectionEnabled ? color + "25" : "#e2e8f0"}`, transition: "all 0.2s", opacity: sectionEnabled ? 1 : 0.65 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: sectionEnabled ? color : "#94a3b8", display: "flex", alignItems: "center", gap: 8 }}>{title}
          {(() => { const bools = regularFields.filter(f => f.type === "bool"); const onN = bools.filter(f => !!profile[f.key]).length; return bools.length > 1 ? <span title="الصلاحيات المفعّلة في هذا القسم" style={{ fontSize: 10, fontWeight: 800, padding: "1px 8px", borderRadius: 999, background: onN ? color + "18" : "#f1f5f9", color: onN ? color : "#94a3b8", fontFamily: "monospace" }}>{onN}/{bools.length}</span> : null; })()}{/* v3.4.712 */}
        </div>
        {masterKey && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 10, fontWeight: 600, color: sectionEnabled ? color : "#94a3b8", padding: "3px 10px", background: "#fff", borderRadius: 12, border: `1px solid ${sectionEnabled ? color + "40" : "#e2e8f0"}` }}>
            <span>{sectionEnabled ? "مُفعَّل" : "معطَّل"}</span>
            <span
              role="switch" aria-checked={sectionEnabled}
              onClick={() => toggleSection(!sectionEnabled)}
              style={{
                position: "relative", display: "inline-block", width: 30, height: 16,
                background: sectionEnabled ? color : "#cbd5e1", borderRadius: 9,
                cursor: "pointer", transition: "background 0.2s"
              }}>
              <span style={{
                position: "absolute", top: 2, [sectionEnabled ? "right" : "left"]: 2,
                width: 12, height: 12, background: "#fff", borderRadius: "50%",
                transition: "all 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
              }}/>
            </span>
          </label>
        )}
      </div>
      {showFields && (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {sortPerms(regularFields, masterKey).map(f => {
          if (f.type === "bool") {
            const isMaster = f.key === masterKey; // v3.4.712 — مفتاح موحّد
            return <PermToggle key={f.key} on={!!profile[f.key]} disabled={!sectionEnabled && !isMaster} master={isMaster} color={color} label={f.label} onChange={(v) => setProfile(p => ({ ...p, [f.key]: v }))} />;
          }
          if (f.type === "days") {
            return (
              <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, opacity: sectionEnabled ? 1 : 0.5 }}>
                <span style={{ fontWeight: 600, color: "#64748b" }}>{f.label}:</span>
                <select value={profile[f.key] ?? 0} disabled={!sectionEnabled}
                  onChange={e => setProfile(p => ({...p, [f.key]: parseInt(e.target.value)}))}
                  style={{ padding: "3px 6px", border: "1px solid #e2e8f0", borderRadius: 5, fontFamily: "inherit", fontSize: 10, background: "#fff" }}>
                  {DAYS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            );
          }
          if (f.type === "text") {
            return (
              <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, flex: "1 1 200px", opacity: sectionEnabled ? 1 : 0.5 }}>
                <span style={{ fontWeight: 600, color: "#64748b", whiteSpace: "nowrap" }}>{f.label}:</span>
                <input value={profile[f.key] || ""} placeholder="فارغ = الكل" disabled={!sectionEnabled}
                  onChange={e => setProfile(p => ({...p, [f.key]: e.target.value}))}
                  style={{ flex: 1, padding: "3px 6px", border: "1px solid #e2e8f0", borderRadius: 5, fontFamily: "inherit", fontSize: 10, minWidth: 80 }} />
              </div>
            );
          }
          // v3.3.339 — scope picker (3 mutually-exclusive options)
          if (f.type === "scope") {
            const opts = f.options || [];
            return (
              <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, flex: "1 1 100%", opacity: sectionEnabled ? 1 : 0.5, marginTop: 4 }}>
                <span style={{ fontWeight: 700, color: "#64748b", whiteSpace: "nowrap" }}>{f.label}:</span>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {opts.map(o => {
                    const active = (profile[f.key] || 'all') === o.value;
                    return (
                      <button type="button" key={o.value} disabled={!sectionEnabled}
                        onClick={() => setProfile(p => ({...p, [f.key]: o.value}))}
                        style={{
                          padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 600, cursor: sectionEnabled ? "pointer" : "default",
                          background: active ? color : "#fff",
                          color: active ? "#fff" : "#64748b",
                          border: `1px solid ${active ? color : "#e2e8f0"}`,
                          fontFamily: "inherit",
                        }}>{o.label}</button>
                    );
                  })}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
      )}
      {/* Columns checkbox grids — مخفية إذا القسم معطل */}
      {sectionEnabled && columnsFields.map(f => (
        <ColumnsCheckboxGrid key={f.key} fieldKey={f.key} columns={f.columns}
          color={color} profile={profile} setProfile={setProfile} />
      ))}
      {/* v3.3.497 — Optional footer (e.g. explanatory text under permissions) */}
      {sectionEnabled && footer}
    </div>
  );
}

// v3.4.220 — المهندس المسؤول عن مشاريع الخرائط: لكل مستخدم قائمة مشاريع (مهندس ↔ عدة مشاريع)؛ التعيين يفتح له
// «مرجع الخرائط التنفيذية» لمشاريعه ويطلب منه تأكيد الاستلام والمراجعة/الاعتماد لكل خريطة
function EngineerProjectsPicker({ user, data, onChange }) {
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const mine = data.assignments.filter(a => String(a.user_id) === String(user.id)).map(a => Number(a.project_id));
  const toggle = async (pid) => {
    const next = mine.includes(pid) ? mine.filter(x => x !== pid) : [...mine, pid];
    setBusy(true);
    try { await constructionApi.setEngineerProjects(user.id, { project_ids: next }); onChange(user.id, next); toast.success(next.length ? "حُدّثت مشاريع المهندس" : "أُزيل من كل المشاريع"); }
    catch (e) { toast.error(e.response?.data?.error || "فشل التعيين"); }
    setBusy(false);
  };
  return (
    <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px dashed #e2e8f0", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span style={{ fontSize: 11, fontWeight: 800, color: "#0f766e", whiteSpace: "nowrap" }}>🗺 مهندس مسؤول عن خرائط:</span>
      {data.projects.length === 0 && <span style={{ fontSize: 10.5, color: "#94a3b8" }}>لا توجد مشاريع في المقاول الرئيسي</span>}
      {data.projects.map(p => {
        const on = mine.includes(Number(p.id));
        return (
          <button key={p.id} type="button" disabled={busy} onClick={() => toggle(Number(p.id))} title={on ? "إزالة المسؤولية عن هذا المشروع" : "تعيينه مهندسًا مسؤولًا عن هذا المشروع"}
            style={{ padding: "3px 10px", borderRadius: 12, fontSize: 10.5, fontWeight: on ? 800 : 600, cursor: busy ? "wait" : "pointer", fontFamily: "inherit",
              background: on ? "#0f766e" : "#fff", color: on ? "#fff" : "#64748b", border: on ? "1px solid #0f766e" : "1px solid #e2e8f0" }}>
            {on ? "✓ " : ""}{p.name}
          </button>
        );
      })}
      {mine.length > 0 && <span style={{ fontSize: 10, color: "#94a3b8" }}>({mine.length} مشروع — يظهر له مرجع الخرائط تلقائيًا)</span>}
    </div>
  );
}

export default function UserManagement() {
  const { user: me, refreshUser } = useAuth();
  const [tab, setTab] = useState("profiles"); // profiles | users
  const [profilesList, setProfilesList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileUsers, setProfileUsers] = useState([]);
  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [showInviteUser, setShowInviteUser] = useState(false);
  // v3.3.350 — بحث المستخدمين بالبريد/الاسم
  const [userSearch, setUserSearch] = useState("");
  const [editingProfile, setEditingProfile] = useState(null);
  // v3.3.419 — Matrix import
  const matrixFileInputRef = React.useRef(null);
  const [matrixImporting, setMatrixImporting] = useState(false);
  
  // Create profile form
  // v3.4.022 — الافتراضي: لا مجموعات — الأدمن يحدد المجموعات الممنوحة عند الإنشاء
  const EMPTY_PROFILE = {
    name: "", name_ar: "", description: "",
    view_scope: "all",
    can_view_dalala_amount: true, can_view_dalala_breakdown: true,
    ...Object.fromEntries(ALL_GROUP_VIEW_KEYS.map(k => [k, false])),
    can_create: false, can_edit_info: false, can_edit_finance: false, can_edit_commission: false,
    can_edit_status_res: false, can_edit_status_acc: false, can_delete: false, can_manage_users: false,
    can_manage_projects: false, can_approve_unit_requests: false,
  };
  const [newProfile, setNewProfile] = useState(EMPTY_PROFILE);
  
  // Invite user form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteProfileId, setInviteProfileId] = useState(null);
  const [operationTypes, setOperationTypes] = useState([]);
  // v3.4.220 — مشاريع الخرائط وتعيينات المهندسين (تُحمّل مرة؛ 403 لغير الأدمن/can_manage_users يُتجاهل)
  const [engData, setEngData] = useState(null);
  useEffect(() => { constructionApi.engineers().then(r => setEngData({ projects: r.data?.projects || [], assignments: r.data?.assignments || [] })).catch(() => {}); }, []);
  const onEngChange = (userId, ids) => setEngData(d => d ? { ...d, assignments: [...d.assignments.filter(a => String(a.user_id) !== String(userId)), ...ids.map(pid => ({ project_id: pid, user_id: userId }))] } : d);

  const fetchProfiles = async () => {
    try {
      const r = await profilesApi.list();
      setProfilesList(r.data.profiles || []);
    } catch (e) { toast.error("فشل تحميل البروفايلات"); }
  };

  const fetchUsers = async () => {
    try {
      const r = await usersApi.list();
      setUserList(r.data.users || []);
    } catch (e) { toast.error("فشل تحميل المستخدمين"); }
  };

  const fetchOptions = async () => {
    try {
      const r = await refApi.getOptions();
      const opts = r.data.options?.operation_type || [];
      setOperationTypes(opts.map(o => o.label || o));
    } catch (e) {}
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchProfiles(), fetchUsers(), fetchOptions()]);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const selectProfile = async (profile) => {
    setSelectedProfile(profile);
    try {
      const r = await profilesApi.get(profile.id);
      setProfileUsers(r.data.users || []);
    } catch (e) { setProfileUsers([]); }
  };

  const handleCreateProfile = async () => {
    if (!newProfile.name || !newProfile.name_ar) return toast.error("الاسم مطلوب");
    try {
      await profilesApi.create({
        name: newProfile.name,
        name_ar: newProfile.name_ar,
        description: newProfile.description,
        permissions: newProfile,
      });
      toast.success("تم إنشاء البروفايل");
      setShowCreateProfile(false);
      setNewProfile(EMPTY_PROFILE);
      fetchProfiles();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  const handleUpdateProfile = async () => {
    if (!editingProfile) return;
    try {
      await profilesApi.update(editingProfile.id, {
        name_ar: editingProfile.name_ar,
        description: editingProfile.description,
        permissions: editingProfile,
      });
      toast.success("تم تحديث البروفايل");
      setEditingProfile(null);
      fetchProfiles();
      if (selectedProfile?.id === editingProfile.id) {
        setSelectedProfile({ ...selectedProfile, ...editingProfile });
      }
    } catch (e) { toast.error(e.response?.data?.error || "فشل التحديث"); }
  };

  // v3.3.543 — Confirmation message is more explicit + better error display.
  // Backend returns user_count when there are users assigned, so we can show it.
  const handleDeleteProfile = async (profile) => {
    const msg = `هل أنت متأكد من حذف بروفايل "${profile.name_ar}"؟\n\nهذا الإجراء نهائي ولا يمكن التراجع عنه.`;
    if (!window.confirm(msg)) return;
    try {
      await profilesApi.delete(profile.id);
      toast.success(`تم حذف بروفايل "${profile.name_ar}"`);
      if (selectedProfile?.id === profile.id) setSelectedProfile(null);
      if (editingProfile?.id === profile.id) setEditingProfile(null);
      fetchProfiles();
    } catch (e) {
      const errMsg = e.response?.data?.error || "فشل الحذف";
      // Display a longer toast for the verbose backend errors (they contain user-actionable instructions)
      toast.error(errMsg, { duration: 7000 });
    }
  };

  // v3.3.545 — handleSyncProfile removed: the backend endpoint is a no-op since v3.3.310
  // (permissions are now read directly from the profile, not copied to each user).
  // The button was misleading because it didn't actually do anything.

  const handleAssignUser = async (userId) => {
    if (!selectedProfile) return;
    try {
      await profilesApi.assign(selectedProfile.id, userId);
      toast.success("تم التعيين");
      selectProfile(selectedProfile);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  const handleUnassignUser = async (userId) => {
    if (!selectedProfile) return;
    try {
      await profilesApi.unassign(selectedProfile.id, userId);
      toast.success("تم إزالة المستخدم من البروفايل");
      selectProfile(selectedProfile);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.includes("@")) return toast.error("بريد إلكتروني غير صالح");
    try {
      const result = await usersApi.invite(inviteEmail, {});
      if (inviteProfileId) {
        await profilesApi.assign(inviteProfileId, result.data.user.id);
      }
      toast.success(`تمت إضافة ${inviteEmail}`);
      setShowInviteUser(false);
      setInviteEmail("");
      setInviteProfileId(null);
      fetchAll();
    } catch (e) {
      const errMsg = e.response?.data?.error || "فشل";
      // v3.3.350 — في حالة "المستخدم موجود بالفعل"، حدّث القائمة لإظهاره ثم أرسل رسالة واضحة
      if (errMsg.includes("موجود")) {
        await fetchUsers();
        toast.error(`${errMsg} — تم تحديث القائمة، ابحث عن: ${inviteEmail}`, { duration: 5000 });
      } else {
        toast.error(errMsg);
      }
    }
  };

  const handleImpersonate = async (profile) => {
    try {
      await authApi.impersonate(profile.id);
      toast.success(`تم تفعيل وضع المعاينة: ${profile.name_ar}`);
      refreshUser();
    } catch (e) { toast.error("فشل"); }
  };

  const handleStopImpersonate = async () => {
    try {
      await authApi.stopImpersonate();
      toast.success("تم إيقاف وضع المعاينة");
      refreshUser();
    } catch (e) { toast.error("فشل"); }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      await usersApi.updateStatus(user.id, !user.is_active);
      toast.success(user.is_active ? "تم تعطيل الحساب" : "تم تفعيل الحساب");
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  const handleRemoveUser = async (user) => {
    if (!window.confirm(`حذف ${user.email}؟`)) return;
    try {
      await usersApi.remove(user.id);
      toast.success("تم الحذف");
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  const getScopeInfo = (scope) => VIEW_SCOPES.find(s => s.value === scope) || VIEW_SCOPES[0];

  if (loading) return <div style={{ padding: 40, color: "#64748b" }}>جاري التحميل...</div>;

  // Impersonation banner
  const impersonationBanner = me?.impersonating && (
    <div style={{
      background: "linear-gradient(90deg, #f59e0b, #d97706)",
      color: "#fff", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center",
      borderRadius: 8, marginBottom: 16, boxShadow: "0 4px 12px rgba(245,158,11,0.3)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <FiEye size={18} />
        <span style={{ fontWeight: 600 }}>وضع المعاينة: {me.impersonating.profile_name}</span>
        <span style={{ fontSize: 12, opacity: 0.9 }}>— ترى النظام كما يراه مستخدم هذا البروفايل</span>
      </div>
      <button onClick={handleStopImpersonate} style={{
        background: "#fff", color: "#d97706", border: "none", padding: "6px 16px",
        borderRadius: 6, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", fontSize: 12,
      }}>إيقاف المعاينة</button>
    </div>
  );

  return (
    <div className="users-page" dir="rtl" style={{ padding: 20 }}>
      {impersonationBanner}
      
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>إدارة المستخدمين والبروفايلات</h1>
          <p style={{ fontSize: 12, color: "#64748b" }}>أنشئ قوالب صلاحيات وعيّن المستخدمين عليها</p>
        </div>
        <div style={{ display: (tab === "profiles" || tab === "users") ? "flex" : "none", gap: 8, flexWrap: "wrap" }}>{/* v3.4.466 — أزرار نظام ERP فقط */}
          {/* v3.3.419 — ماتريكس الصلاحيات (Excel) */}
          <button
            onClick={async () => {
              try { await profilesApi.exportMatrix(); }
              catch (e) { alert("فشل التصدير: " + (e?.response?.data?.error || e.message)); }
            }}
            title="تصدير ماتريكس الصلاحيات إلى Excel"
            style={{
              display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
              background: "#0891b2", color: "#fff", border: "none", borderRadius: 7,
              fontFamily: "inherit", fontSize: 12, cursor: "pointer", fontWeight: 700,
            }}
          ><FiDownload /> تصدير ماتريكس</button>
          <input
            ref={matrixFileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file) return;
              if (!window.confirm(
                "⚠ سيتم تحديث صلاحيات جميع البروفايلات بناءً على الملف.\n\n" +
                "هل تريد المتابعة؟"
              )) return;
              setMatrixImporting(true);
              try {
                const r = await profilesApi.importMatrix(file);
                const d = r.data;
                let msg = `✓ تم تحديث ${d.updated} بروفايل`;
                if (d.skipped > 0) msg += ` · تم تجاهل ${d.skipped}`;
                alert(msg + (d.messages?.length ? "\n\n" + d.messages.join("\n") : ""));
                await fetchProfiles(); /* v3.4.439 — كانت loadProfiles (غير معرّفة): الاستيراد ينجح ثم يظهر «فشل الاستيراد» */
              } catch (err) {
                alert("فشل الاستيراد: " + (err?.response?.data?.error || err.message));
              } finally {
                setMatrixImporting(false);
              }
            }}
          />
          <button
            onClick={() => matrixFileInputRef.current?.click()}
            disabled={matrixImporting}
            title="استيراد ماتريكس الصلاحيات من Excel"
            style={{
              display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
              background: matrixImporting ? "#94a3b8" : "#7c3aed", color: "#fff", border: "none", borderRadius: 7,
              fontFamily: "inherit", fontSize: 12, cursor: matrixImporting ? "wait" : "pointer", fontWeight: 700,
            }}
          ><FiUpload /> {matrixImporting ? "جاري..." : "استيراد ماتريكس"}</button>

          <button onClick={() => setShowInviteUser(true)} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
            background: "#10b981", color: "#fff", border: "none", borderRadius: 7,
            fontFamily: "inherit", fontSize: 13, cursor: "pointer",
          }}><FiUserPlus /> إضافة مستخدم</button>
          <button onClick={() => setShowCreateProfile(true)} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
            background: "#2563eb", color: "#fff", border: "none", borderRadius: 7,
            fontFamily: "inherit", fontSize: 13, cursor: "pointer",
          }}><FiPlus /> إنشاء بروفايل</button>
        </div>
      </div>

      {/* v3.4.466 — ثلاث تبويبات رئيسية: نظام ERP / المنظم التسويقي / إدارة المشاريع */}
      {(() => { const main = tab === "portal" ? "portal" : tab === "pm" ? "pm" : "erp"; return (
        <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          {[["erp", "🖥️", "صلاحيات نظام ERP", "البروفايلات والمستخدمون", "#1e3a8a", "#eff6ff"], ["portal", "📱", "صلاحيات المنظم التسويقي", "تطبيق المسوّقين والموظفين", "#0f766e", "#f0fdfa"], ["pm", "🏗️", "صلاحيات إدارة المشاريع", "تطبيق المهندسين والمحاسبين", "#b45309", "#fffbeb"]].map(([k, ic, l, sub, c, bg]) => { const on = main === k; return (
            <button key={k} type="button" onClick={() => setTab(k === "erp" ? (main === "erp" ? tab : "profiles") : k)}
              style={{ flex: "1 1 220px", display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderRadius: 12, cursor: "pointer", fontFamily: "inherit", textAlign: "right",
                border: "2px solid " + (on ? c : "#e2e8f0"), background: on ? bg : "#fff", boxShadow: on ? "0 4px 14px rgba(15,23,42,.08)" : "none" }}>
              <span style={{ fontSize: 22 }}>{ic}</span>
              <span><span style={{ display: "block", fontSize: 14, fontWeight: 900, color: on ? c : "#334155" }}>{l}</span><span style={{ display: "block", fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{sub}</span></span>
            </button>); })}
        </div>); })()}
      {/* Tabs — v3.4.466: التبويبات الفرعية لنظام ERP */}
      <div style={{ display: (tab === "profiles" || tab === "users") ? "flex" : "none", gap: 4, marginBottom: 16, background: "#f1f5f9", padding: 4, borderRadius: 8, width: "fit-content" }}>
        <button onClick={() => setTab("profiles")} style={{
          padding: "8px 20px", border: "none", borderRadius: 6, fontFamily: "inherit", fontSize: 13, cursor: "pointer",
          background: tab === "profiles" ? "#fff" : "transparent", color: tab === "profiles" ? "#1e293b" : "#64748b",
          fontWeight: tab === "profiles" ? 600 : 400, boxShadow: tab === "profiles" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
        }}>البروفايلات ({profilesList.length})</button>
        <button onClick={() => setTab("users")} style={{
          padding: "8px 20px", border: "none", borderRadius: 6, fontFamily: "inherit", fontSize: 13, cursor: "pointer",
          background: tab === "users" ? "#fff" : "transparent", color: tab === "users" ? "#1e293b" : "#64748b",
          fontWeight: tab === "users" ? 600 : 400, boxShadow: tab === "users" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
        }}>المستخدمين ({userList.length})</button>
      </div>
      {tab === "portal" && <PortalPermissionsPanel />}
      {tab === "pm" && <PmPermissionsPanel />}{/* v3.4.466 */}

      {/* Profiles Tab */}
      {tab === "profiles" && (
        <div style={{ display: "grid", gridTemplateColumns: selectedProfile ? "1fr 1fr" : "1fr", gap: 16 }}>
          {/* Profiles List */}
          <div>
            {/* v3.4.384 — grid of compact boxes (single column while a profile is open beside it) */}
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: selectedProfile ? "1fr" : "repeat(auto-fill, minmax(300px, 1fr))", alignItems: "start" }}>
              {profilesList.map(profile => {
                const scope = getScopeInfo(profile.view_scope);
                const isSelected = selectedProfile?.id === profile.id;
                return (
                  <div key={profile.id} onClick={() => selectProfile(profile)} style={{
                    background: "#fff", border: `2px solid ${isSelected ? "#2563eb" : "#e2e8f0"}`,
                    borderRadius: 10, padding: 14, cursor: "pointer", transition: "all 0.15s",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>{profile.name_ar}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{profile.description}</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ background: `${scope.color}15`, color: scope.color, padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 600 }}>
                          {scope.label}
                        </span>
                        <span style={{ background: "#f1f5f9", color: "#64748b", padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 600 }}>
                          <FiUsers size={10} style={{ marginLeft: 4 }} />{profile.user_count || 0}
                        </span>
                      </div>
                    </div>
                    
                    {/* Quick permissions preview */}
                    <div style={{ display: "flex", gap: 4, marginTop: 10, flexWrap: "wrap" }}>
                      {profile.can_view_dalala_amount && <span style={{ fontSize: 9, background: "#dcfce7", color: "#166534", padding: "2px 6px", borderRadius: 8 }}>💰 الدلالة</span>}
                      {profile.can_create && <span style={{ fontSize: 9, background: "#dbeafe", color: "#1e40af", padding: "2px 6px", borderRadius: 8 }}>➕ إضافة</span>}
                      {profile.can_edit_info && <span style={{ fontSize: 9, background: "#fef3c7", color: "#92400e", padding: "2px 6px", borderRadius: 8 }}>✏️ تعديل</span>}
                      {profile.can_delete && <span style={{ fontSize: 9, background: "#fee2e2", color: "#991b1b", padding: "2px 6px", borderRadius: 8 }}>🗑️ حذف</span>}
                    </div>

                    {/* v3.3.545 — Inline list of users assigned to this profile */}
                    {profile.users && profile.users.length > 0 ? (
                      <div style={{ marginTop: 10, padding: "8px 10px", background: "#f8fafc", borderRadius: 6, border: "1px solid #e2e8f0" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
                          <FiUsers size={11} /> المستخدمين تحت هذا البروفايل ({profile.users.length})
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {profile.users.map(u => (
                            <span key={u.id} title={u.email} style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              padding: "3px 8px", background: u.is_active ? "#fff" : "#f1f5f9",
                              border: "1px solid " + (u.is_active ? "#cbd5e1" : "#e2e8f0"),
                              borderRadius: 12, fontSize: 10,
                              color: u.is_active ? "#0f172a" : "#94a3b8",
                            }}>
                              {u.avatar_url ? (
                                <img src={u.avatar_url} alt="" style={{ width: 14, height: 14, borderRadius: "50%" }} />
                              ) : (
                                <span style={{ width: 14, height: 14, borderRadius: "50%", background: "#cbd5e1", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: "#fff", fontWeight: 700 }}>
                                  {(u.display_name || u.email || "?").charAt(0).toUpperCase()}
                                </span>
                              )}
                              {u.display_name || u.email}
                              {!u.is_active && <span style={{ fontSize: 8, color: "#94a3b8" }}>(معطّل)</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div style={{ marginTop: 10, padding: "6px 10px", background: "#fafbfc", borderRadius: 6, fontSize: 10, color: "#94a3b8", textAlign: "center" }}>
                        لا يوجد مستخدمين تحت هذا البروفايل
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div style={{ display: "flex", gap: 6, marginTop: 10 }} onClick={e => e.stopPropagation()}>
                      {(me?.role === "admin" || me?.users_can_preview) && ( /* v3.4.243 — الزر بصلاحية */
                      <button onClick={() => handleImpersonate(profile)} title="معاينة كمستخدم" style={{
                        display: "flex", alignItems: "center", gap: 4, padding: "5px 10px",
                        background: "#fef3c7", color: "#92400e", border: "none", borderRadius: 6, fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                      }}><FiPlay size={12} /> معاينة</button>
                      )}
                      <button onClick={() => setEditingProfile({...profile})} style={{
                        display: "flex", alignItems: "center", gap: 4, padding: "5px 10px",
                        background: "#f1f5f9", border: "none", borderRadius: 6, fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                      }}><FiSettings size={12} /> تعديل</button>
                      {/* v3.3.545 — Sync button removed: it was a no-op since v3.3.310.
                          Permissions are read live from the profile, so no manual sync is needed. */}
                      {/* v3.3.543 — Delete button now shown for ALL profiles (including
                          is_system ones). The backend enforces safety rules: cannot delete
                          a profile with users assigned, cannot delete your own current profile,
                          cannot delete the last user-management profile. */}
                      <button onClick={() => handleDeleteProfile(profile)} title="حذف البروفايل" style={{
                        padding: "5px 10px", background: "#fee2e2", color: "#ef4444", border: "none", borderRadius: 6, fontSize: 11, cursor: "pointer",
                      }}><FiTrash2 size={12} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Profile Details Panel */}
          {selectedProfile && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{selectedProfile.name_ar}</h3>
                <button onClick={() => setSelectedProfile(null)} style={{
                  background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 18,
                }}>✕</button>
              </div>

              {/* Users in this profile */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>المستخدمين ({profileUsers.length})</div>
                {profileUsers.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#94a3b8", padding: 12, background: "#f8fafc", borderRadius: 8, textAlign: "center" }}>
                    لا يوجد مستخدمين
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {profileUsers.map(u => (
                      <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: "#f8fafc", borderRadius: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {u.avatar_url ? <img src={u.avatar_url} alt="" style={{ width: 28, height: 28, borderRadius: "50%" }} /> :
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600 }}>
                              {u.display_name?.charAt(0) || "?"}
                            </div>}
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{u.display_name}</div>
                            <div style={{ fontSize: 10, color: "#94a3b8" }}>{u.email}{u.wa_phone ? <span dir="ltr" style={{ marginInlineStart: 6, color: "#16a34a" }}>📱 {u.wa_phone}</span> : null}</div>
                          </div>
                        </div>
                        {/* v3.4.728 — جوال واتساب الموظف: به يتعرف الرقم الموحد على الموظف عند تحويل أرقام المسوقين */}
                        <button onClick={async () => { const v = window.prompt(`جوال واتساب الموظف ${u.display_name} (يُستخدم لحصر عملاء المسوقين عبر الرقم الموحد — اتركه فارغًا للإزالة):`, u.wa_phone || ""); if (v === null) return; try { await usersApi.setWaPhone(u.id, v); toast.success("حُفظ الجوال"); fetchUsers(); } catch (e) { toast.error(e?.response?.data?.error || "تعذّر الحفظ"); } }} title="جوال واتساب الموظف" style={{
                          padding: "4px 8px", background: "#f0fdf4", color: "#166534", border: "none", borderRadius: 4, fontSize: 10, cursor: "pointer", marginInlineEnd: 4,
                        }}>📱</button>
                        <button onClick={() => handleUnassignUser(u.id)} style={{
                          padding: "4px 8px", background: "#fee2e2", color: "#ef4444", border: "none", borderRadius: 4, fontSize: 10, cursor: "pointer",
                        }}>إزالة</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add user to profile */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>إضافة مستخدم للبروفايل</div>
                <select 
                  onChange={(e) => { if (e.target.value) handleAssignUser(e.target.value); e.target.value = ""; }}
                  style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12 }}
                >
                  <option value="">— اختر مستخدم —</option>
                  {userList.filter(u => !profileUsers.some(pu => pu.id === u.id) && u.id !== me.id).map(u => (
                    <option key={u.id} value={u.id}>{u.display_name} ({u.email})</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {tab === "users" && (
        <div style={{ display: "grid", gap: 10 }}>
          {/* v3.3.350 — شريط بحث + زر تحديث */}
          <div style={{ display: "flex", gap: 8, marginBottom: 4, alignItems: "center" }}>
            <input
              type="text"
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              placeholder="🔎 ابحث بالبريد أو الاسم..."
              style={{ flex: 1, padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, fontFamily: "inherit", direction: "rtl" }}
            />
            <button onClick={fetchUsers} style={{ padding: "8px 14px", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", color: "#475569" }}>
              🔄 تحديث القائمة
            </button>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>عدد: {userList.length}</span>
          </div>
          {(() => {
            const q = userSearch.trim().toLowerCase();
            const filtered = q ? userList.filter(u =>
              (u.email || "").toLowerCase().includes(q) ||
              (u.display_name || "").toLowerCase().includes(q)
            ) : userList;
            if (filtered.length === 0 && q) {
              return <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, padding: 12, fontSize: 12, color: "#854d0e", textAlign: "center" }}>
                لا يوجد مستخدم مطابق لـ "{userSearch}"
              </div>;
            }
            return filtered.map(u => {
            const isMe = u.id === me.id;
            const profile = profilesList.find(p => p.id === u.profile_id);
            return (
              <div key={u.id} style={{
                background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {u.avatar_url ? <img src={u.avatar_url} alt="" style={{ width: 40, height: 40, borderRadius: "50%" }} /> :
                      <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600 }}>
                        {u.display_name?.charAt(0) || "?"}
                      </div>}
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {u.display_name}
                        {isMe && <span style={{ color: "#2563eb", fontSize: 11, marginRight: 6 }}>(أنت)</span>}
                        {u.role === "admin" && <span style={{ marginRight: 6, padding: "2px 6px", borderRadius: 10, fontSize: 9, background: "#dbeafe", color: "#1e40af", fontWeight: 600 }}>أدمن</span>}
                      </div>
                      <div style={{ fontSize: 11, color: "#94a3b8" }}>{u.email}</div>
                      {!u.has_logged_in && <span style={{ fontSize: 9, color: "#f59e0b", display: "flex", alignItems: "center", gap: 2 }}><FiMail size={10} /> لم يسجل دخول بعد</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {/* Profile badge */}
                    {profile ? (
                      <span style={{ background: "#eff6ff", color: "#1e40af", padding: "4px 12px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                        {profile.name_ar}
                      </span>
                    ) : (
                      <span style={{ background: "#f1f5f9", color: "#94a3b8", padding: "4px 12px", borderRadius: 12, fontSize: 11 }}>
                        بدون بروفايل
                      </span>
                    )}
                    {/* Status */}
                    <span style={{
                      padding: "4px 10px", borderRadius: 12, fontSize: 10, fontWeight: 600,
                      background: u.is_active ? "#dcfce7" : "#fee2e2",
                      color: u.is_active ? "#166534" : "#991b1b",
                    }}>{u.is_active ? "فعّال" : "معطّل"}</span>
                  </div>
                </div>

                {/* Actions */}
                {!isMe && (
                  <div style={{ display: "flex", gap: 6, marginTop: 10, alignItems: "center" }}>
                    {/* Assign to profile */}
                    <select 
                      value={u.profile_id || ""}
                      onChange={(e) => {
                        if (e.target.value) {
                          profilesApi.assign(e.target.value, u.id).then(() => { toast.success("تم التعيين"); fetchUsers(); });
                        } else if (u.profile_id) {
                          profilesApi.unassign(u.profile_id, u.id).then(() => { toast.success("تم إزالة البروفايل"); fetchUsers(); });
                        }
                      }}
                      style={{ padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 11, minWidth: 150 }}
                    >
                      <option value="">— بدون بروفايل —</option>
                      {profilesList.map(p => <option key={p.id} value={p.id}>{p.name_ar}</option>)}
                    </select>
                    {/* v3.4.754 — جوال واتساب الموظف (تخويل تحويل أرقام المسوقين إلى الرقم الموحد 920012054) */}
                    <button title={u.wa_phone ? `جوال واتساب المربوط: ${u.wa_phone} — اضغط للتعديل` : "اربط جوال واتساب الموظف ليستطيع تحويل أرقام المسوقين إلى الرقم الموحد"}
                      onClick={async () => { const v = window.prompt(`جوال واتساب الموظف ${u.display_name}\nبه يتعرّف الرقم الموحد 920012054 على الموظف عند تحويل أرقام المسوقين (اتركه فارغًا للإزالة):`, u.wa_phone || ""); if (v === null) return; try { await usersApi.setWaPhone(u.id, v); toast.success(v.trim() ? "حُفظ جوال واتساب — يستطيع الآن تحويل الأرقام إلى الرقم الموحد" : "أُزيل الجوال"); fetchUsers(); } catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); } }}
                      style={{ padding: "5px 10px", background: u.wa_phone ? "#dcfce7" : "#fff7ed", color: u.wa_phone ? "#166534" : "#9a3412", border: `1px solid ${u.wa_phone ? "#bbf7d0" : "#fed7aa"}`, borderRadius: 6, fontSize: 11, cursor: "pointer", fontFamily: "inherit", direction: "ltr" }}>
                      📱 {u.wa_phone || "ربط واتساب"}
                    </button>
                    <button onClick={() => handleToggleUserStatus(u)} style={{
                      padding: "5px 12px", background: u.is_active ? "#fef2f2" : "#dcfce7",
                      color: u.is_active ? "#ef4444" : "#22c55e", border: "none", borderRadius: 6, fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                    }}>{u.is_active ? "تعطيل" : "تفعيل"}</button>
                    <button onClick={() => handleRemoveUser(u)} style={{
                      padding: "5px 10px", background: "#fff", color: "#ef4444", border: "1px solid #fca5a5", borderRadius: 6, fontSize: 11, cursor: "pointer",
                    }}><FiTrash2 size={12} /></button>
                  </div>
                )}
                {u.role !== "admin" && <EngineerProjectsPicker user={u} data={engData} onChange={onEngChange} />}{/* v3.4.220 */}
              </div>
            );
          });
          })()}
        </div>
      )}

      {/* Create Profile Modal */}
      {showCreateProfile && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowCreateProfile(false)}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: 600, maxWidth: "90vw", maxHeight: "90vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>إنشاء بروفايل جديد</h3>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>الاسم (English)</label>
                <input value={newProfile.name} onChange={e => setNewProfile(p => ({...p, name: e.target.value}))}
                  placeholder="marketing_manager"
                  style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 13, marginTop: 4 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>الاسم (عربي)</label>
                <input value={newProfile.name_ar} onChange={e => setNewProfile(p => ({...p, name_ar: e.target.value}))}
                  placeholder="مدير التسويق"
                  style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 13, marginTop: 4 }} />
              </div>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>الوصف</label>
              <input value={newProfile.description} onChange={e => setNewProfile(p => ({...p, description: e.target.value}))}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 13, marginTop: 4 }} />
            </div>

            {/* v3.3.339: تم نقل "نطاق العرض" إلى داخل قسم الحجوزات (حقل "أنواع العمليات") */}

            {/* v3.4.022 — بدل القائمة المسطحة: تحديد مجموعات القائمة الجانبية الممنوحة */}
            <GroupsSelector value={newProfile}
              onChange={patch => setNewProfile(pr => ({ ...pr, ...patch }))}
              hint="بعد الإنشاء تُضبط الصلاحيات التفصيلية (إضافة/تعديل/حذف…) من «تعديل» — تظهر فقط أقسام المجموعات الممنوحة" />

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowCreateProfile(false)} style={{
                padding: "8px 20px", background: "#f1f5f9", border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: 13,
              }}>إلغاء</button>
              <button onClick={handleCreateProfile} style={{
                padding: "8px 24px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600,
              }}>إنشاء</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {editingProfile && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setEditingProfile(null)}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: 720, maxWidth: "95vw", maxHeight: "90vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>تعديل: {editingProfile.name_ar}</h3>
            
            <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>الاسم (عربي)</label>
                <input value={editingProfile.name_ar} onChange={e => setEditingProfile(p => ({...p, name_ar: e.target.value}))}
                  style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 13, marginTop: 4 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>الوصف</label>
                <input value={editingProfile.description || ""} onChange={e => setEditingProfile(p => ({...p, description: e.target.value}))}
                  style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 13, marginTop: 4 }} />
              </div>
            </div>

            {/* v3.3.339: تم نقل "نطاق العرض (فلتر نوع العملية)" إلى داخل قسم الحجوزات (حقل "أنواع العمليات") */}

            {/* v3.4.022 — المجموعات الممنوحة: تفعيل/إلغاء مجموعة يظهر/يخفي أقسام صلاحياتها أدناه */}
            <GroupsSelector value={editingProfile}
              onChange={patch => setEditingProfile(pr => ({ ...pr, ...patch }))}
              hint="تظهر أدناه فقط أقسام المجموعات الممنوحة — حدد داخلها الصلاحيات التفصيلية" />

            {/* ═══════ MODULE-LEVEL PERMISSIONS ═══════ */}
            <div style={{ borderTop: "2px solid #e2e8f0", paddingTop: 16, marginTop: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: "#1e40af", margin: 0 }}>صلاحيات الأقسام (تفصيلي)</h4>
                {/* v3.3.355 — أزرار سريعة لإدارة كل الأقسام دفعة واحدة */}
                <div style={{ display: "flex", gap: 6 }}>
                  <button type="button"
                    onClick={() => {
                      const allViewKeys = [
                        "can_view_reservations","can_view_clients","can_view_developers","can_view_marketers",
                        "can_view_projects","can_view_client_groups","can_view_marketer_groups","can_view_prospect_groups",
                        "can_view_prospects","can_view_contracts","can_view_client_comm","can_view_marketer_comm",
                        "can_view_business_comm","can_view_settings","can_view_reservations_scheduler","can_view_payments",
                        "can_view_collections","can_view_collections_stats","can_view_analytics","can_view_financial_reports",
                        "can_view_commissions","can_view_cash_flow","can_view_cash_flow_statement","can_view_benchmarking","can_view_company_assets","can_view_capex","can_view_audit","can_view_wa_abubader2","can_view_wa_abubader_biz",
                      ];
                      setEditingProfile(p => {
                        const next = { ...p };
                        allViewKeys.forEach(k => { next[k] = false; });
                        return next;
                      });
                      toast.success("تم تعطيل كل أقسام العرض — حدّد ما تريد إظهاره");
                    }}
                    style={{ padding: "5px 12px", background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    🚫 تعطيل كل الأقسام
                  </button>
                  <button type="button"
                    onClick={() => {
                      const allViewKeys = [
                        "can_view_reservations","can_view_clients","can_view_developers","can_view_marketers",
                        "can_view_projects","can_view_client_groups","can_view_marketer_groups","can_view_prospect_groups",
                        "can_view_prospects","can_view_contracts","can_view_client_comm","can_view_marketer_comm",
                        "can_view_business_comm","can_view_settings","can_view_reservations_scheduler","can_view_payments",
                        "can_view_collections","can_view_collections_stats","can_view_analytics","can_view_financial_reports",
                        "can_view_commissions","can_view_cash_flow","can_view_cash_flow_statement","can_view_benchmarking","can_view_company_assets","can_view_capex","can_view_audit","can_view_wa_abubader2","can_view_wa_abubader_biz",
                      ];
                      setEditingProfile(p => {
                        const next = { ...p };
                        allViewKeys.forEach(k => { next[k] = true; });
                        return next;
                      });
                      toast.success("تم تفعيل كل الأقسام");
                    }}
                    style={{ padding: "5px 12px", background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    ✅ تفعيل كل الأقسام
                  </button>
                </div>
              </div>
              {/* رسالة توضيحية */}
              <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "8px 12px", marginBottom: 12, fontSize: 11, color: "#854d0e", lineHeight: 1.6 }}>
                💡 <strong>ملاحظة:</strong> اضغط على toggle "مُفعَّل/معطَّل" بجانب اسم كل قسم لإخفاء/إظهار العنصر في القائمة الجانبية للمستخدم. الأقسام المعطَّلة لن تظهر للمستخدم.
              </div>

              {/* v3.4.024 — الأقسام مجمّعة داخل صناديق المجموعات (إطار كامل لكل مجموعة، تختفي كليًا عند عدم منحها) */}
              {/* v3.4.348 — Chairman Office (مكتب رئيس المجلس) */}
              <GroupBox gkey="chairman" profile={editingProfile}>
              <ModulePermsSection title="👔 Chairman Office — مكتب رئيس مجلس الإدارة" color="#0f766e" mainKey="can_view_chairman_office"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_chairman_office", label: "👔 الوصول إلى مكتب الرئيس: الإيميلات (الصندوق الموحّد)، التقويم الموحّد، التواصل الخارجي (واتساب الرئيس)، Chairman Contacts", type: "bool" },
                ]} />
              </GroupBox>

              {/* v3.4.835 — Alma Capital مجموعة مستقلة (كانت بندًا داخل Chairman Office في v3.4.822) + صلاحية لكل تبويب */}
              <GroupBox gkey="alma" profile={editingProfile}>
              <ModulePermsSection title="🏛️ Alma Capital — موقع ألما الإلكتروني" color="#0f766e" mainKey="can_view_alma"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_alma", label: "الوصول إلى قسم Alma Capital (التبويبات تُحدَّد أدناه — بلا تبويب ممنوح يرى القسم فارغًا)", type: "bool" },
                  { key: "alma_can_manage", label: "✏️ تعديل البيانات في التبويبات الممنوحة ونشرها على الموقع", type: "bool" },
                  { key: "alma_can_view_content", label: "تبويب 🌐 محتوى الموقع الإلكتروني", type: "bool" },
                  { key: "alma_can_view_messages", label: "تبويب 📥 الرسائل الواردة", type: "bool" },
                  { key: "alma_can_view_compliance", label: "تبويب ✅ الالتزام التنظيمي (CMA)", type: "bool" },
                  { key: "alma_can_view_people", label: "تبويب 👥 القيادة والأشخاص", type: "bool" },
                  { key: "alma_can_view_startup", label: "تبويب 📋 متابعة متطلبات بدء ممارسة العمل", type: "bool" },
                  { key: "alma_can_view_settings", label: "تبويب ⚙️ بيانات التواصل والموقع", type: "bool" },
                ]} />
              </GroupBox>

              <GroupBox gkey="office" profile={editingProfile}>
              {/* v3.3.740 — قسم سجل زوار المكتب */}
              <ModulePermsSection title="🚪 قسم سجل زوار المكتب" color="#7c3aed" mainKey="can_view_office_visitors"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_office_visitors", label: "عرض وتسجيل", type: "bool" },
                ]} />
              {/* v3.4.180 — واتس الرقم الرئيسي للشركة */}
              <ModulePermsSection title="📍 واتس رقم دليلك الرسمي" color="#0f766e" mainKey="can_view_daleelak_whatsapp"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_daleelak_whatsapp", label: "عرض محادثات رقم دليلك الرسمي والرد عليها", type: "bool" },
                ]} />{/* v3.4.587 */}
              <ModulePermsSection title="🏢 واتس الرقم الرئيسي للشركة" color="#16a34a" mainKey="can_view_company_whatsapp"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_company_whatsapp", label: "عرض محادثات الرقم الرئيسي والرد عليها", type: "bool" },
                  { key: "company_wa_can_view_all", label: "عرض كل المحادثات (بدونها يرى المحوّلة إليه فقط)", type: "bool" }, // v3.4.841
                ]} />
              {/* v3.4.207 — قائمة المهام للموظفين */}
              <ModulePermsSection title="✅ قائمة المهام للموظفين" color="#0f766e" mainKey="can_view_tasks"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_tasks", label: "عرض القائمة — ويضيف مهامًا لنفسه (بدء / إقفال بملاحظة / ملاحظات)", type: "bool" },
                  { key: "tasks_can_assign", label: "إضافة مهام للغير (الإسناد) ومتابعة كل المهام (تعديل / حذف / إعادة فتح)", type: "bool" },
                ]} />
              <ModulePermsSection title="📄 الخطابات الرسمية — إعداد الخطابات على ورق الشركات بأختام وتواقيع" color="#0f766e" mainKey="can_view_letters"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_letters", label: "عرض الخطابات وإنشاؤها وطباعتها ومشاركتها", type: "bool" },
                  { key: "letters_can_manage", label: "✍️ إدارة القوالب والأختام والتواقيع + حذف الخطابات", type: "bool" },
                ]} />{/* v3.4.815 */}
              </GroupBox>

              <GroupBox gkey="reservations|projects" profile={editingProfile}>{/* v3.4.348 — أقسام المشاريع داخل هذا الصندوق */}
              {/* Reservations Module */}
              {/* v3.4.202 — العروض التسويقية — v3.4.205: داخل تبويب تسويق المشروع (مجموعة الحجوزات/المشاريع) */}
              <ModulePermsSection title="🏷️ العروض التسويقية (داخل تبويب تسويق المشروع)" color="#0f766e" mainKey="can_view_marketing_offers"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_marketing_offers", label: "عرض", type: "bool" },
                  { key: "marketing_offers_can_manage", label: "إضافة / تعديل / حذف العروض ورفع البروشور", type: "bool" },
                  { key: "marketing_offers_can_share", label: "مشاركة العروض مع المسوقين (واتساب)", type: "bool" },
                ]} />
              <ModulePermsSection title="قسم الحجوزات" color="#2563eb" mainKey="can_view_reservations"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_reservations", label: "عرض", type: "bool" },
                  { key: "can_create", label: "إضافة", type: "bool" },
                  { key: "res_can_export", label: "تصدير", type: "bool" },
                  { key: "res_can_import", label: "استيراد", type: "bool" },
                  { key: "res_can_delete", label: "حذف", type: "bool" },
                  { key: "res_edit_days", label: "مدة التعديل (أيام)", type: "days" },
                  { key: "res_operation_types", label: "أنواع العمليات (فاصلة)", type: "text" },
                  // v3.3.339 — حصر نطاق رؤية الحجوزات
                  { key: "res_scope_filter", label: "🌐 حصر النطاق", type: "scope", options: [
                    { value: "all", label: "الكل" },
                    { value: "daleel", label: "حجوزات دليلك فقط" },
                    { value: "company", label: "حجوزات الشركة فقط" },
                  ]},
                  { key: "res_visible_columns", label: "أعمدة ظاهرة", type: "columns", columns: RES_COLUMNS },
                  // v3.3.324: بطاقات الإحصائيات في صفحة الحجوزات
                  { key: "can_view_reservations_total_value", label: "💰 بطاقة إجمالي القيمة", type: "bool" },
                  { key: "can_view_reservations_total_dalala", label: "💎 بطاقة إجمالي الدلالة", type: "bool" },
                ]} />

              {/* Projects Module — Custom grouped layout */}
              {(() => {
                const color = "#0891b2";
                const ep = editingProfile;
                const setEp = setEditingProfile;
                // v3.4.023 — يظهر فقط إذا كانت مجموعة الحجوزات ممنوحة
                if (!groupOn(ep, SIDEBAR_GROUPS.find(g => g.views.includes("can_view_projects")))) return null;
                const boolChip = (key, label) => <PermToggle key={key} on={!!ep[key]} color={color} label={label} onChange={(v) => setEp(p => ({ ...p, [key]: v }))} />; // v3.4.712
                const groupTitle = (title) => ( // v3.4.712 — عنوان قسم فرعي
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", marginTop: 12, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}><span style={{ width: 4, height: 14, background: color, borderRadius: 2, display: "inline-block" }} />{title}</div>
                );
                return (
                  (() => {
                    // v3.3.312: Section master toggle (hybrid behavior)
                    const masterKey = "can_view_projects";
                    const sectionEnabled = !!ep[masterKey];
                    const toggleSection = (enabled) => {
                      setEp(p => {
                        const next = { ...p };
                        if (enabled) {
                          next[masterKey] = true;
                        } else {
                          // Disable all bool fields in this section
                          const boolKeys = ["can_view_projects","projects_can_export","projects_can_import","projects_can_create","projects_can_edit","projects_can_add_building","projects_can_add_unit","projects_can_bulk_edit","projects_can_view_stats","projects_can_view_unit_details","projects_can_edit_unit","projects_can_change_unit_status","projects_can_view_financial_study","projects_can_view_marketing","projects_can_manage_marketing","projects_can_view_reports","projects_can_create_reports","projects_can_send_reports_whatsapp","projects_can_view_unit_payments","projects_can_add_unit_payments","projects_can_delete_unit_payments","projects_can_edit_admin_fees","projects_can_view_admin_fees","projects_can_view_marketer_dalala","projects_can_link_reservation","projects_can_request_reservation","projects_can_view_documents","projects_can_download_documents","projects_can_view_rega","projects_can_view_completeness"];
                          for (const k of boolKeys) next[k] = false;
                        }
                        return next;
                      });
                    };
                    return (
                  <div style={{ marginBottom: 12, padding: 12, background: sectionEnabled ? color + "08" : "#f8fafc", borderRadius: 8, border: `1px solid ${sectionEnabled ? color + "25" : "#e2e8f0"}`, transition: "all 0.2s", opacity: sectionEnabled ? 1 : 0.65 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: sectionEnabled ? color : "#94a3b8" }}>قسم المشاريع</div>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 10, fontWeight: 600, color: sectionEnabled ? color : "#94a3b8", padding: "3px 10px", background: "#fff", borderRadius: 12, border: `1px solid ${sectionEnabled ? color + "40" : "#e2e8f0"}` }}>
                        <span>{sectionEnabled ? "مُفعَّل" : "معطَّل"}</span>
                        <span role="switch" aria-checked={sectionEnabled} onClick={() => toggleSection(!sectionEnabled)}
                          style={{ position: "relative", display: "inline-block", width: 30, height: 16, background: sectionEnabled ? color : "#cbd5e1", borderRadius: 9, cursor: "pointer", transition: "background 0.2s" }}>
                          <span style={{ position: "absolute", top: 2, [sectionEnabled ? "right" : "left"]: 2, width: 12, height: 12, background: "#fff", borderRadius: "50%", transition: "all 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }}/>
                        </span>
                      </label>
                    </div>
                    {sectionEnabled && (<>

                    {/* Row 1: العرض والتحكم */}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 4 }}>
                      {boolChip("can_view_projects", "عرض")}
                      {boolChip("projects_can_export", "تصدير")}
                      {boolChip("projects_can_import", "استيراد")}
                      {boolChip("projects_can_create", "مشروع جديد")}
                      {boolChip("projects_can_edit", "تعديل المشروع")}
                      {boolChip("projects_can_add_building", "إضافة مبنى")}
                      {boolChip("projects_can_add_unit", "إضافة وحدة")}
                      {boolChip("projects_can_bulk_edit", "تعديل جدول الوحدات")}
                      {boolChip("projects_can_view_stats", "إحصائيات المشروع")}
                    </div>

                    {/* Group: تفاصيل الوحدة */}
                    {groupTitle("🏠 تفاصيل الوحدة")}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 4 }}>
                      {boolChip("projects_can_view_unit_details", "عرض تفاصيل الوحدة")}
                      {boolChip("projects_can_edit_unit", "تعديل بيانات الوحدة (الحالة + التفاصيل)")}
                      {boolChip("projects_can_change_unit_status", "🔄 تغيير حالة الوحدة فقط (متاح/مباع/محجوز)")}
                      {boolChip("projects_can_view_financial_study", "📊 عرض الدراسة المالية (السعر الرسمي/التنفيذ/الصافي)")}
                      {boolChip("projects_can_view_marketing", "🎯 عرض تسويق المشروع")}
                      {boolChip("projects_can_manage_marketing", "🎯 إدارة/رفع تسويق المشروع")}
                      {/* v3.3.349 — صلاحيات تحديثات المشروع */}
                      {boolChip("projects_can_view_reports", "📊 عرض تحديثات المشروع")}
                      {boolChip("projects_can_create_reports", "📊 إنشاء/رفع تحديثات وتقارير")}
                      {boolChip("projects_can_send_reports_whatsapp", "📲 إرسال التحديثات عبر الواتساب")}
                      {/* v3.3.568 — Inline notice: this permission also grants view-access
                          to the marketers WhatsApp channel (groups list), since the user
                          needs to see the groups to pick which ones to broadcast to. */}
                      {editingProfile?.projects_can_send_reports_whatsapp && !editingProfile?.can_view_marketer_comm && (
                        <div style={{
                          fontSize: 9, color: "#1e40af", background: "#eff6ff",
                          border: "1px solid #bfdbfe", borderRadius: 4,
                          padding: "3px 8px", marginTop: 4, lineHeight: 1.5,
                        }}>
                          💡 ملاحظة: تفعيل هذه الصلاحية يمنح تلقائياً رؤية جروبات الواتساب لقناة المسوقين (تلقائياً عند الإرسال).
                        </div>
                      )}
                      {/* v3.3.351 — عرض قسم دفعات السداد
                          v3.3.667 — boolChip تأخذ (key, label) فقط؛ كانت تُمرَّر مفاتيح إضافية
                          فيظهر اسم مفتاح آخر كتسمية (حذف الدفعات كان معروضاً بتسمية
                          projects_can_edit_admin_fees فتعذّر على الأدمن إيجاده) */}
                      {boolChip("projects_can_view_unit_payments", "💳 عرض قسم دفعات السداد")}
                      {/* v3.3.357 — إدارة دفعات الوحدة */}
                      {boolChip("projects_can_add_unit_payments", "➕ إضافة دفعات للوحدة")}
                      {boolChip("projects_can_delete_unit_payments", "🗑️ حذف دفعات الوحدة")}
                      {/* v3.3.358 — تعديل الرسوم الإدارية */}
                      {boolChip("projects_can_edit_admin_fees", "💼 تعديل الرسوم الإدارية ورسوم الشركة")}
                      {boolChip("projects_can_view_admin_fees", "💰 عرض الرسوم الإدارية")}
                      {boolChip("projects_can_view_marketer_dalala", "📣 عرض المسوّق وتوزيع الدلالة")}
                    </div>
                    {/* Sub-group: بيانات الوحدة */}
                    <div style={{ fontSize: 9, fontWeight: 600, color: "#94a3b8", marginTop: 6, marginBottom: 4 }}>بيانات الوحدة</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", padding: 8, background: "#fff", borderRadius: 6, border: "1px solid #e2e8f0" }}>
                      {[
                        { key: "building", label: "المبنى" },
                        { key: "floor", label: "الدور" },
                        { key: "unit_code", label: "رمز الوحدة" },
                        { key: "unit_number", label: "رقم الوحد" },
                        { key: "facade", label: "الواجهة" },
                        { key: "rooms", label: "عدد الغرف" },
                        { key: "bathrooms", label: "عدد الحمام" },
                        { key: "kitchen", label: "المطبخ" },
                        { key: "living", label: "الصالة" },
                        { key: "area", label: "المساحة" },
                        { key: "price", label: "السعر الرسمي" },
                        { key: "unit_status", label: "حالة الوحدة" },
                      ].map(col => {
                        const currentVal = (ep.projects_visible_columns || "").trim();
                        const selectedKeys = currentVal ? currentVal.split(",").map(s => s.trim()).filter(Boolean) : [];
                        const allSelected = selectedKeys.length === 0;
                        const checked = allSelected || selectedKeys.includes(col.key);
                        return (
                          <label key={col.key} style={{
                            display: "flex", alignItems: "center", gap: 3, padding: "2px 8px",
                            background: checked ? color + "12" : "#f8fafc",
                            border: `1px solid ${checked ? color + "40" : "#e2e8f0"}`,
                            borderRadius: 12, cursor: "pointer", fontSize: 10, fontWeight: 500,
                            color: checked ? color : "#94a3b8", userSelect: "none",
                          }}>
                            <input type="checkbox" checked={checked}
                              onChange={() => {
                                if (allSelected) {
                                  const newKeys = PROJECTS_COLUMNS.filter(c => c.key !== col.key).map(c => c.key);
                                  setEp(p => ({...p, projects_visible_columns: newKeys.join(",")}));
                                } else if (selectedKeys.includes(col.key)) {
                                  const newKeys = selectedKeys.filter(k => k !== col.key);
                                  setEp(p => ({...p, projects_visible_columns: newKeys.length === 0 ? "" : newKeys.join(",")}));
                                } else {
                                  const newKeys = [...selectedKeys, col.key];
                                  setEp(p => ({...p, projects_visible_columns: newKeys.length >= PROJECTS_COLUMNS.length ? "" : newKeys.join(",")}));
                                }
                              }}
                              style={{ width: 11, height: 11, accentColor: color }} />
                            {col.label}
                          </label>
                        );
                      })}
                    </div>

                    {/* Sub-group: ربط الحجز */}
                    {groupTitle("🔗 ربط الحجز")}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      {boolChip("projects_can_link_reservation", "ربط بحجز موجود")}
                      {boolChip("projects_can_request_reservation", "طلب حجز جديد")}
                    </div>

                    {/* Sub-group: المستندات */}
                    {groupTitle("📄 المستندات")}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      {boolChip("projects_can_view_documents", "عرض المستندات")}
                      {boolChip("projects_can_download_documents", "تحميل المستندات")}
                      {boolChip("projects_can_view_completeness", "🧾 فحص اكتمال الإجراءات")}{/* v3.4.136 */}
                      {boolChip("projects_can_view_rega", "🏛️ تبويب REGA & SAKANI")}{/* v3.4.284 */}
                    </div>
                    </>)}
                  </div>
                    );
                  })()
                );
              })()}

              </GroupBox>

              <GroupBox gkey="media" profile={editingProfile}>{/* v3.4.361 */}
              <ModulePermsSection title="🎨 مكتبة التصاميم والجرافيك — مستودع فريق التصميم" color="#7c3aed" mainKey="can_view_media_library"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_media_library", label: "🎨 عرض مكتبة التصاميم والجرافيك: المحتوى الدعائي العام ومكتبة كل مشروع (البروشورات، تصاميم السور، الفيديو، الشاشة، البوستات، النماذج، العروض، المناظير)", type: "bool" },
                  { key: "media_library_can_manage", label: "⬆️ الرفع والتعديل والحذف وإنشاء أقسام إضافية (فريق التصميم والجرافيك) — بدونها المكتبة للاستعراض فقط", type: "bool" },
                ]} />
              {/* v3.4.547 — مرجع الحسابات الإلكترونية */}
              <ModulePermsSection title="🗝️ مرجع الحسابات الإلكترونية — الدومينات وحسابات السوشل والبريد وبيانات الدخول" color="#0f766e" mainKey="can_view_digital_accounts"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_digital_accounts", label: "عرض القائمة والروابط وأسماء المستخدمين (بلا كلمات مرور)", type: "bool" },
                  { key: "digital_accounts_can_reveal", label: "👁 كشف كلمات المرور (كل كشف يُسجَّل في سجل التدقيق)", type: "bool" },
                  { key: "digital_accounts_can_manage", label: "✍️ إضافة وتعديل وحذف الحسابات", type: "bool" },
                ]} />
              <ModulePermsSection title="📣 إدارة السوشل ميديا — حسابات الشركات على المنصات والحملات" color="#7c3aed" mainKey="can_view_social_media"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_social_media", label: "📣 عرض إدارة السوشل ميديا: حسابات رئال / دليلك / أصول على كل المنصات، الربط بالـAPI، والحملات", type: "bool" },
                  { key: "social_media_can_manage", label: "✏️ إضافة وتعديل وحذف الحسابات وربطها بالـAPI وإدارة الحسابات الإعلانية — بدونها الصفحة للاستعراض فقط", type: "bool" },
                ]} />{/* v3.4.389 */}
              {/* v3.4.489 — المواقع الإلكترونية — v3.4.796: داخل مجموعة سوشل ميديا وجرافيك */}
              <ModulePermsSection title="🌐 المواقع الإلكترونية (رئال / دليلك / أصول)" color="#0369a1" mainKey="can_view_websites"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_websites", label: "عرض المواقع ومحتواها وإعداداتها", type: "bool" },
                  { key: "websites_can_edit", label: "✍️ تعديل محتوى وإعدادات المواقع", type: "bool" },
                ]} />
              </GroupBox>

              <GroupBox gkey="ops" profile={editingProfile}>{/* v3.4.496 — صندوق واحد بترتيب القائمة: تنسيق الحجوزات، المدفوعات، التحصيلات، تحليل التسويق، ثم العقود وصرف الدلالات */}
              {/* v3.3.609 — قسم العمليات التشغيلية مقسّم لثلاثة أقسام مستقلة */}
              {/* v3.3.759 — قسم تحليل التسويق الداخلي */}
              <ModulePermsSection title="📈 قسم تحليل التسويق الداخلي" color="#0d9488" mainKey="can_view_marketing_analytics"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_marketing_analytics", label: "عرض وإدارة", type: "bool" },
                ]} />

              <ModulePermsSection title="📅 قسم تنسيق الحجوزات" color="#0891b2" mainKey="can_view_reservations_scheduler"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_reservations_scheduler", label: "عرض", type: "bool" },
                  { key: "can_view_sched_card_total", label: "🧾 بطاقة إجمالي الحجوزات", type: "bool" }, // v3.4.486
                  { key: "can_view_sched_card_value", label: "💰 بطاقة إجمالي القيمة", type: "bool" }, // v3.4.486
                  { key: "can_view_sched_card_expected_month", label: "💸 بطاقة التحصيل المتوقع هذا الشهر", type: "bool" }, // v3.4.486
                  { key: "can_view_sched_card_expected_next", label: "🗓️ بطاقة التحصيل المتوقع الشهر القادم", type: "bool" }, // v3.4.486
                  { key: "can_view_sched_card_overdue", label: "⚠️ بطاقة متأخر", type: "bool" }, // v3.4.486
                  { key: "can_view_sched_card_today", label: "🔥 بطاقة اليوم", type: "bool" }, // v3.4.486
                  { key: "can_view_sched_card_week", label: "📅 بطاقة هذا الأسبوع", type: "bool" }, // v3.4.486
                  { key: "can_view_sched_card_no_date", label: "⏳ بطاقة بدون موعد", type: "bool" }, // v3.4.486
                ]} />

              <ModulePermsSection title="💳 قسم المدفوعات" color="#0d9488" mainKey="can_view_payments"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_payments", label: "عرض", type: "bool" },
                  { key: "can_view_payments_stats", label: "📊 بطاقات الإحصائيات (المحصّل، عدد المدفوعات، العقود الجديدة والمبيعات، التحصيل، إعادة البيع)", type: "bool" }, // v3.4.476
                  { key: "can_view_payments_timeline", label: "📈 الحركة الزمنية للتحصيل والمبيعات", type: "bool" },
                  { key: "can_view_payments_schedule", label: "📅 جدول السداد مقابل المُحصّل فعليًا", type: "bool" },
                ]} />

              <ModulePermsSection title="💰 قسم التحصيلات والديون" color="#dc2626" mainKey="can_view_collections"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_collections", label: "عرض", type: "bool" },
                  { key: "can_view_collections_stats", label: "📊 إحصائيات التحصيلات (البطاقات العلوية)", type: "bool" },
                  // v3.3.341 — صلاحيات منفصلة لبطاقات التحصيلات
                  { key: "can_view_collections_units_sold", label: "🏠 بطاقة الوحدات المباعة", type: "bool" },
                  { key: "can_view_collections_total_value", label: "💰 بطاقة قيمة المبيعات", type: "bool" },
                  { key: "can_view_collections_total_paid", label: "💵 بطاقة المُحصّل", type: "bool" },
                  // v3.3.608 — بقية مكوّنات صفحة التحصيلات
                  { key: "can_view_collections_remaining", label: "🔴 بطاقة المتبقي", type: "bool" },
                  { key: "can_view_collections_units_with_debt", label: "🟣 بطاقة وحدات بمتبقي", type: "bool" },
                  { key: "can_view_collections_overdue", label: "🟠 بطاقة متأخرات مستحقة", type: "bool" },
                  { key: "can_view_collections_timeline", label: "📈 الرسم البياني (الحركة الزمنية)", type: "bool" },
                  { key: "collections_can_edit_contract_date", label: "✍️ تعديل تاريخ توقيع العقد", type: "bool" }, // v3.4.227
                  { key: "collections_can_mark_expected", label: "✅ تعليم «متوقع سداده هذا الشهر» (العمود الأول)", type: "bool" }, // v3.4.239
                ]} />

              {/* v3.4.541 — المبيعات */}
              {/* v3.4.561 — عروض الأسعار */}
              <ModulePermsSection title="📄 قسم عروض الأسعار — عرض سعر لوحدة (نوع العقد، السعر والخصم، الدفعات) يُعتمد ثم يُطبع" color="#0f766e" mainKey="can_view_quotations"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_quotations", label: "عرض صفحة عروض الأسعار", type: "bool" },
                  { key: "quotations_can_create", label: "✍️ إنشاء وتعديل عروض الأسعار وإرسالها للاعتماد", type: "bool" },
                  { key: "quotations_can_approve", label: "✅ اعتماد / رفض عروض الأسعار (المدير العام / المدير التنفيذي)", type: "bool" },
                  { key: "quotations_can_manage_settings", label: "⚙️ إدارة نظام العروض (النسب والرسوم حسب برنامج الدفعات، طرق وخيارات الدفع)", type: "bool" }, /* v3.4.577 */
                ]} />
              <ModulePermsSection title="🏷️ قسم المبيعات" color="#ea580c" mainKey="can_view_sales"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_sales", label: "عرض صفحة المبيعات", type: "bool" },
                  { key: "sales_can_view_list", label: "تبويب قائمة المبيعات", type: "bool" },
                  { key: "sales_can_view_analysis", label: "تبويب تحليل المبيعات", type: "bool" },
                  { key: "sales_can_view_stats", label: "بطاقات الإحصاءات (العدد/القيمة/المساحة/سعر المتر/المُحصّل)", type: "bool" },
                  { key: "sales_can_view_charts", label: "المخططات (الشهرية وعدد المبيعات)", type: "bool" },
                  { key: "sales_can_view_projects", label: "جدول المشاريع وسعر متر البيع", type: "bool" },
                  { key: "sales_can_view_prices", label: "أعمدة الأسعار (سعر البيع/الرسمي/الخصم/المُحصّل)", type: "bool" },
                  { key: "sales_can_view_sqm_price", label: "عمود سعر المتر", type: "bool" }, // v3.4.542
                  { key: "sales_can_view_client", label: "عمود العميل", type: "bool" },
                  { key: "sales_can_view_marketer", label: "عمود المسوّق", type: "bool" },
                  { key: "sales_can_export", label: "تصدير Excel", type: "bool" },
                ]} />

              {/* العقود */}
              <ModulePermsSection title="📄 قسم العقود" color="#0d9488" mainKey="can_view_contracts"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_contracts", label: "عرض", type: "bool" },
                  { key: "contracts_can_add", label: "إضافة", type: "bool" },
                  { key: "contracts_can_edit", label: "تعديل", type: "bool" },
                  { key: "contracts_can_delete", label: "حذف", type: "bool" },
                  { key: "contracts_can_export", label: "تصدير", type: "bool" },
                  { key: "contracts_can_edit_templates", label: "🛠 تعديل قوالب العقود (المباشر / وافي / الحقول)", type: "bool" }, // v3.4.530
                ]} />

              {/* Disbursements Section - قسم صرف الدلالات */}
              {(() => {
                const color = "#059669";
                const ep = editingProfile;
                const setEp = setEditingProfile;
                // v3.4.023 — يظهر فقط إذا كانت مجموعة الإجراءات ممنوحة
                if (!groupOn(ep, SIDEBAR_GROUPS.find(g => g.views.includes("can_view_disbursements")))) return null;
                const boolChip = (key, label) => <PermToggle key={key} on={!!ep[key]} color={color} label={label} onChange={(v) => setEp(p => ({ ...p, [key]: v }))} />; // v3.4.712
                return (
                  (() => {
                    // v3.3.312: Section master toggle
                    const masterKey = "can_view_disbursements";
                    const sectionEnabled = !!ep[masterKey];
                    const toggleSection = (enabled) => {
                      setEp(p => {
                        const next = { ...p };
                        if (enabled) {
                          next[masterKey] = true;
                        } else {
                          const boolKeys = ["can_view_disbursements","can_request_disbursement","can_approve_disbursement","can_execute_disbursement","can_delete_disbursement"];
                          for (const k of boolKeys) next[k] = false;
                        }
                        return next;
                      });
                    };
                    return (
                  <div style={{ marginBottom: 12, padding: 12, background: sectionEnabled ? color + "08" : "#f8fafc", borderRadius: 8, border: `1px solid ${sectionEnabled ? color + "25" : "#e2e8f0"}`, transition: "all 0.2s", opacity: sectionEnabled ? 1 : 0.65 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: sectionEnabled ? color : "#94a3b8" }}>💰 قسم صرف الدلالات</div>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 10, fontWeight: 600, color: sectionEnabled ? color : "#94a3b8", padding: "3px 10px", background: "#fff", borderRadius: 12, border: `1px solid ${sectionEnabled ? color + "40" : "#e2e8f0"}` }}>
                        <span>{sectionEnabled ? "مُفعَّل" : "معطَّل"}</span>
                        <span role="switch" aria-checked={sectionEnabled} onClick={() => toggleSection(!sectionEnabled)}
                          style={{ position: "relative", display: "inline-block", width: 30, height: 16, background: sectionEnabled ? color : "#cbd5e1", borderRadius: 9, cursor: "pointer", transition: "background 0.2s" }}>
                          <span style={{ position: "absolute", top: 2, [sectionEnabled ? "right" : "left"]: 2, width: 12, height: 12, background: "#fff", borderRadius: "50%", transition: "all 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }}/>
                        </span>
                      </label>
                    </div>
                    {sectionEnabled && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      {boolChip("can_view_disbursements", "عرض")}
                      {boolChip("can_request_disbursement", "طلب صرف")}
                      {boolChip("can_approve_disbursement", "تعميد الإدارة")}
                      {boolChip("can_execute_disbursement", "تأكيد المحاسبة")}
                      {boolChip("can_delete_disbursement", "حذف الطلبات")}
                    </div>
                    )}
                  </div>
                    );
                  })()
                );
              })()}

              </GroupBox>

              <GroupBox gkey="vouchers" profile={editingProfile}>{/* v3.4.612 — صندوق مستقل بترتيب القائمة الجانبية (كان داخل صندوق العمليات التشغيلية) */}
              {/* v3.4.611 — سندات القبض والصرف */}
              <ModulePermsSection title="🧾 قسم سندات القبض والصرف — تُصدر من النظام وتُرسل إلى دفترة وتُطبع" color="#b45309" mainKey="can_view_vouchers"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_vouchers", label: "عرض صفحة سندات القبض والصرف", type: "bool" },
                  { key: "vouchers_can_create", label: "✍️ إصدار سندات قبض/صرف وإرسالها إلى دفترة وإلغاء ما لم يُرسل", type: "bool" },
                  { key: "vouchers_can_manage", label: "⚙️ إعدادات السندات (الفرع، الخزائن، رقم السند) وإلغاء السندات المرسلة", type: "bool" },
                  { key: "vouchers_can_edit", label: "✏️ تعديل أي سند بانتظار الاعتماد (مُصدر السند يعدّل سنده دائمًا)", type: "bool" }, // v3.4.687
                  { key: "vouchers_can_approve", label: "✅ اعتماد السندات (المحاسب) — بعده يُرسل إلى دفترة ويُسجَّل على الوحدة ويُحفظ PDF في مستنداتها", type: "bool" }, // v3.4.687
                ]} />
              </GroupBox>

              <GroupBox gkey="hr" profile={editingProfile}>
              {/* v3.4.192 — الموارد البشرية */}
              <ModulePermsSection title="👥 قسم الموارد البشرية" color="#0f766e" mainKey="can_view_hr"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_hr", label: "عرض (الموظفون / الحضور / الطلبات / الأرصدة / التقرير)", type: "bool" },
                  { key: "hr_can_view_applicants", label: "🧑‍💼 عرض المتقدمين للوظائف (مكتبة السير الذاتية)", type: "bool" }, // v3.4.543
                  { key: "hr_can_manage_applicants", label: "🧑‍💼 إدارة المتقدمين: إضافة/تعديل/حذف ورفع السير الذاتية", type: "bool" },
                  { key: "hr_can_manage", label: "إدارة: إضافة وتعديل الموظفين والمواقع، اعتماد الطلبات، الإعدادات", type: "bool" },
                  { key: "hr_can_edit_attendance", label: "🕒 تعديل/حذف سجل الحضور والانصراف (تصحيح يدوي) — بدونها للأدمن فقط", type: "bool" }, /* v3.4.559 */
                ]} />
              </GroupBox>

              <GroupBox gkey="legal" profile={editingProfile}>{/* v3.4.420 */}
              <ModulePermsSection title="⚖️ الإدارة القانونية" color="#7c3aed" mainKey="can_view_legal"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_legal", label: "عرض: مكتبة العقود، سجل القضايا، سجل المخالفات البلدية، مكتبة السجلات والعلامات التجارية، والتنبيهات", type: "bool" },
                  { key: "legal_can_manage", label: "إدارة: إضافة وتعديل وحذف السجلات، رفع المرفقات، وتسجيل أحداث القضايا", type: "bool" },
                ]} />
              </GroupBox>

              <GroupBox gkey="comm" profile={editingProfile}>
              {/* تواصل العملاء */}
              <ModulePermsSection title="📞 تواصل واتس مباشر · تواصل العملاء" color="#0284c7" mainKey="can_view_client_comm"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_client_comm", label: "عرض", type: "bool" },
                  { key: "client_comm_can_send", label: "إرسال رسائل", type: "bool" },
                  { key: "client_comm_can_delete", label: "حذف", type: "bool" },
                ]} />

              {/* تواصل المسوقين */}
              <ModulePermsSection title="📞 تواصل واتس مباشر · تواصل المسوقين" color="#ca8a04" mainKey="can_view_marketer_comm"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_marketer_comm", label: "عرض القناة الأساسية", type: "bool" },
                  { key: "marketer_comm_can_send", label: "إرسال رسائل", type: "bool" },
                  { key: "marketer_comm_can_delete", label: "حذف", type: "bool" },
                  // v3.3.356 — قنوات واتساب منفصلة
                  { key: "can_view_wa_abubader2", label: "📱 تواصل ابوبدر 2", type: "bool" },
                  { key: "can_view_wa_abubader_biz", label: "💼 تواصل ابوبدر بزنس", type: "bool" },
                ]} />

              <ModulePermsSection title="📱 جلسات واتس المباشر (رقم المسوقين / رقم العملاء)" color="#16a34a" mainKey="wa_session_can_manage"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "wa_session_can_manage", label: "إدارة الجلسة: ربط الرقم بالرمز، تحديث الحالة، إعادة التشغيل، التنظيف العميق، فصل الرقم", type: "bool" },
                ]} />{/* v3.4.747 — كانت للأدمن فقط */}
              {/* التواصل المؤسسي */}
              <ModulePermsSection title="💬 التواصل المؤسسي" color="#7c3aed" mainKey="can_view_business_comm"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_business_comm", label: "عرض", type: "bool" },
                  { key: "business_comm_can_send", label: "إرسال", type: "bool" },
                  { key: "business_comm_can_manage", label: "إدارة", type: "bool" },
                ]} />

              {/* v3.4.439 — نُقلا من صندوق العمليات التشغيلية (كانا خارج صندوق التواصل الخارجي منذ v3.4.410) */}
              <ModulePermsSection title="📢 إدارة جروب التحديثات (واتس)" color="#0f766e" mainKey="can_view_wa_updates_group"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_wa_updates_group", label: "عرض صفحة إدارة جروب التحديثات (النماذج والإرسال، الاستراتيجية، سجل النشر، مكتبة المحتوى)", type: "bool" },
                  { key: "wa_updates_can_edit_strategy", label: "تعديل الاستراتيجية (الدورة الأسبوعية والدليل والآلية)", type: "bool" },
                ]} />{/* v3.4.410 */}
              <ModulePermsSection title="🛡️ إدارة الواتس" color="#0f766e" mainKey="can_view_wa_admin"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_wa_admin", label: "عرض صفحة إدارة الواتس (حدود الأرقام اليومية وإجراءات الحماية)", type: "bool" },
                  { key: "wa_admin_can_manage", label: "تعديل الحدود وإجراءات الحماية", type: "bool" },
                ]} />{/* v3.4.744 — كانت الصفحة مربوطة بإدارة المستخدمين فقط */}
              <ModulePermsSection title="🎟️ الدعوات" color="#7c3aed" mainKey="can_view_invitations"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_invitations", label: "عرض صفحة الدعوات", type: "bool" },
                  { key: "invitations_can_send", label: "إرسال الدعوات", type: "bool" },
                ]} />{/* v3.4.410 */}

              </GroupBox>

              <GroupBox gkey="data" profile={editingProfile}>
              {/* v3.3.343 — مجموعات المحتملين — v3.4.186: القسمان هنا تحت «البيانات» (كانا تحت نظام التسويق) */}
              <ModulePermsSection title="👥 قسم مجموعات مسوقين محتملين" color="#ec4899" mainKey="can_view_prospect_groups"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_prospect_groups", label: "عرض", type: "bool" },
                  // v3.3.865 — كانت موجودة بالباكند وغائبة من الواجهة
                  { key: "prosp_groups_can_add", label: "إضافة مجموعة", type: "bool" },
                  { key: "prosp_groups_can_edit", label: "تعديل", type: "bool" },
                  { key: "prosp_groups_can_delete", label: "حذف", type: "bool" },
                ]} />

              {/* المحتملين */}
              <ModulePermsSection title="🎯 قسم مسوقين محتملين" color="#ec4899" mainKey="can_view_prospects"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_prospects", label: "عرض", type: "bool" },
                  { key: "prospects_can_add", label: "إضافة", type: "bool" },
                  { key: "prospects_can_edit", label: "تعديل", type: "bool" },
                  { key: "prospects_can_delete", label: "حذف", type: "bool" },
                  { key: "prospects_can_export", label: "تصدير", type: "bool" },
                  { key: "prospects_can_import", label: "استيراد", type: "bool" },
                ]} />
              {/* Clients Module */}
              <ModulePermsSection title="قسم العملاء" color="#059669" mainKey="can_view_clients"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_clients", label: "عرض", type: "bool" },
                  { key: "clients_can_add", label: "إضافة", type: "bool" },
                  { key: "clients_can_export", label: "تصدير", type: "bool" },
                  { key: "clients_can_import", label: "استيراد", type: "bool" },
                  { key: "clients_full_edit", label: "تعديل كامل", type: "bool" },
                  { key: "clients_edit_days", label: "مدة التعديل (أيام)", type: "days" },
                  { key: "clients_can_view_history", label: "سجل الحجوزات", type: "bool" },
                  { key: "clients_can_view_stats", label: "إحصائيات", type: "bool" },
                  { key: "clients_visible_columns", label: "أعمدة ظاهرة", type: "columns", columns: CLIENTS_COLUMNS },
                ]} />

              {/* Developers Module */}
              <ModulePermsSection title="قسم المطورين" color="#7c3aed" mainKey="can_view_developers"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_developers", label: "عرض", type: "bool" },
                  { key: "devs_can_add", label: "إضافة", type: "bool" },
                  { key: "devs_can_export", label: "تصدير", type: "bool" },
                  { key: "devs_can_import", label: "استيراد", type: "bool" },
                  { key: "devs_full_edit", label: "تعديل كامل", type: "bool" },
                  { key: "devs_edit_days", label: "مدة التعديل (أيام)", type: "days" },
                  { key: "devs_can_view_history", label: "سجل الحجوزات", type: "bool" },
                  { key: "devs_can_view_stats", label: "إحصائيات", type: "bool" },
                  { key: "devs_visible_columns", label: "أعمدة ظاهرة", type: "columns", columns: DEVS_COLUMNS },
                ]} />

              {/* Marketers Module */}
              <ModulePermsSection title="قسم المسوقين" color="#d97706" mainKey="can_view_marketers"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_marketers", label: "عرض", type: "bool" },
                  { key: "mkts_can_add", label: "إضافة", type: "bool" },
                  { key: "mkts_can_export", label: "تصدير", type: "bool" },
                  { key: "mkts_can_import", label: "استيراد", type: "bool" },
                  { key: "mkts_full_edit", label: "تعديل كامل", type: "bool" },
                  { key: "mkts_edit_days", label: "مدة التعديل (أيام)", type: "days" },
                  { key: "mkts_can_view_history", label: "سجل الحجوزات", type: "bool" },
                  { key: "mkts_can_view_stats", label: "إحصائيات", type: "bool" },
                  { key: "mkts_visible_columns", label: "أعمدة ظاهرة", type: "columns", columns: MKTS_COLUMNS },
                ]} />

              {/* مجموعات العملاء */}
              <ModulePermsSection title="👥 قسم مجموعات العملاء" color="#6366f1" mainKey="can_view_client_groups"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_client_groups", label: "عرض", type: "bool" },
                  { key: "client_groups_can_add", label: "إضافة مجموعة", type: "bool" },
                  { key: "client_groups_can_edit", label: "تعديل", type: "bool" },
                  { key: "client_groups_can_delete", label: "حذف", type: "bool" },
                ]} />

              {/* مجموعات المسوقين */}
              <ModulePermsSection title="👥 قسم مجموعات المسوقين" color="#a855f7" mainKey="can_view_marketer_groups"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_marketer_groups", label: "عرض", type: "bool" },
                  { key: "mktr_groups_can_add", label: "إضافة مجموعة", type: "bool" },
                  { key: "mktr_groups_can_edit", label: "تعديل", type: "bool" },
                  { key: "mktr_groups_can_delete", label: "حذف", type: "bool" },
                ]} />

              </GroupBox>

              <GroupBox gkey="marketing" profile={editingProfile}>
              {/* v3.3.820 — العملاء المحتملون (البوابة/المناديب/المسوّقين) — v3.4.186: علم مستقل can_view_client_prospects */}
              <ModulePermsSection title="👥 قسم العملاء المحتملين (البوابة والمناديب)" color="#0f766e" mainKey="can_view_client_prospects"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_client_prospects", label: "عرض القائمة", type: "bool" },
                  { key: "cp_can_promote", label: "ترحيل من وارد المناديب للقائمة العامة", type: "bool" },
                  { key: "cp_can_assign", label: "توزيع العملاء على المسوّقين", type: "bool" },
                  { key: "cp_can_export", label: "تصدير/استيراد Excel", type: "bool" },
                  { key: "cp_can_delete", label: "حذف عميل محتمل", type: "bool" },
                  { key: "cp_can_view_stats", label: "تاب إحصائيات المناديب", type: "bool" },
                  { key: "cp_can_view_dalilak_reservations", label: "🏠 عرض تاب «حجوزات دليلك»", type: "bool" }, // v3.4.241
                  { key: "cp_can_add_dalilak_reservations", label: "🏠 إضافة حجوزات دليلك (مدير التسويق)", type: "bool" }, // v3.4.236
                  { key: "cp_can_view_sales_dashboard", label: "💰 عرض تاب «داشبورد المبيعات»", type: "bool" }, // v3.4.241
                  { key: "cp_dalilak_window_days", label: "🗓 نافذة حجوزات دليلك — يرى آخر (N) يوم فقط: 30 / 60 / 90 / 120 — و«ممنوع/غير محدود» = الكل؛ ما هو بانتظار إكمال الإجراءات يظهر دائمًا", type: "days" }, // v3.4.240
                  { key: "cp_dalilak_exclude_direct", label: "🚫 استثناء «عميل شركة مباشر» و«ابوريما» من حجوزات دليلك", type: "bool" }, // v3.4.242
                  { key: "cp_can_chat", label: "تاب خدمة المناديب (شات)", type: "bool" },
                  { key: "cp_can_manage_accounts", label: "تاب إدارة حسابات البوابة", type: "bool" },
                ]} />

              {/* v3.4.107 — كل عنصر في مجموعة «نظام التسويق» بالقائمة الجانبية له علم عرض مستقل (كانت كلها تحت «عرض القائمة») */}
              <ModulePermsSection title="📱 تطبيق المنظم التسويقي" color="#0f766e" mainKey="can_view_portal_app"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_portal_app", label: "رابط تطبيق المنظم التسويقي", type: "bool" },
                ]} />
              <ModulePermsSection title="🏷️ عروض الأراضي" color="#0f766e" mainKey="can_view_land_offers"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_land_offers", label: "عرض", type: "bool" },
                  { key: "land_can_manage", label: "إضافة/تعديل/حذف (الأراضي والمشاريع)", type: "bool" },
                ]} />
              <ModulePermsSection title="🏷️ إعلانات عقار — متابعة إعلانات الشركة على منصة عقار" color="#0f766e" mainKey="can_view_aqar_listings"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_aqar_listings", label: "عرض الإعلانات المتابَعة وحالتها وسجل تغيّراتها", type: "bool" },
                  { key: "aqar_can_manage", label: "إضافة إعلانات للمتابعة، الفحص اليدوي، الحذف، والربط بالمشاريع", type: "bool" },
                ]} />{/* v3.4.399 */}
              <ModulePermsSection title="🏠 عروض المشاريع" color="#0f766e" mainKey="can_view_project_offers"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_project_offers", label: "عرض (ويضيف عروضًا ويعدّل/يحذف ما أضافه هو)", type: "bool" },
                  { key: "project_offers_can_manage", label: "تعديل وحذف كل العروض (وليس عروضه فقط)", type: "bool" }, // v3.4.184
                ]} />
              <ModulePermsSection title="💬 إدارة البزنس شات" color="#0369a1" mainKey="can_view_business_chat"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_business_chat", label: "عرض محادثات واتساب الأعمال (كل الأرقام)", type: "bool" },
                  // v3.4.114 — تفصيل التبويبات
                  { key: "bizchat_can_reply", label: "الرد على المحادثات (من رقم الأعمال)", type: "bool" },
                  { key: "bizchat_can_view_marketers", label: "تاب واتس المسوقين (الأرقام الشخصية المربوطة)", type: "bool" },
                  { key: "bizchat_can_meta", label: "تاب إعدادات ميتا (التشخيص والاشتراك — التوكن للأدمن فقط)", type: "bool" },
                ]} />

              {/* v3.3.923 — إدارة الكول سنتر (Yeastar) */}
              <ModulePermsSection title="☎️ قسم إدارة الكول سنتر (Yeastar)" color="#0369a1" mainKey="can_view_callcenter"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_callcenter", label: "الوصول لإدارة الكول سنتر (التحويلات/المكالمات/التسجيلات)", type: "bool" },
                  { key: "callcenter_can_settings", label: "عرض تاب الإعدادات (التحويلات الداخلية / توجيه الشرائح / ربط السنترال / الجايتواي)", type: "bool" }, // v3.4.177
                ]} />

              </GroupBox>

              <GroupBox gkey="construction" profile={editingProfile}>
              {/* v3.3.849 / v3.4.025 — إدارة المشاريع: قسم مستقل لكل صفحة من صفحات المجموعة */}
              <ModulePermsSection title="🏗 المقاول الرئيسي للمشاريع" color="#b45309" mainKey="can_view_construction"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_construction", label: "عرض القسم (المشاريع/العمال/الملخصات/التصنيفات)", type: "bool" },
                  { key: "construction_can_edit", label: "إضافة/تعديل/حذف (حضور، مواد، مقاولون، عقود…)", type: "bool" },
                  { key: "pm_can_manage_accounts", label: "تطبيق إدارة المشاريع: إدارة الحسابات (اعتماد المستخدمين)", type: "bool" }, // v3.3.979
                ]} />
              <ModulePermsSection title="💵 أوامر صرف اليوميات الأسبوعية" color="#166534" mainKey="can_view_weekly_orders"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_weekly_orders", label: "عرض الصفحة", type: "bool" },
                  { key: "construction_can_approve", label: "تم التعميد / تم الصرف (للمحاسب)", type: "bool" },
                ]} />
              <ModulePermsSection title="💰 عقود وأعمال مقاولي الباطن" color="#0e7490" mainKey="can_view_subcontracts"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_subcontracts", label: "عرض الصفحة (العقود والدفعات والمستخلصات)", type: "bool" },
                ]} />
              {/* v3.4.621 — عقود التوريدات والخدمات الاستشارية */}
              <ModulePermsSection title="📦 عقود التوريدات والخدمات الاستشارية" color="#9333ea" mainKey="can_view_supply_contracts"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_supply_contracts", label: "عرض الصفحة (المشاريع والعقود والملفات وأوامر الصرف)", type: "bool" },
                  { key: "supply_contracts_can_edit", label: "إضافة وتعديل العقود، رفع الملفات وعروض الأسعار، جدول الدفعات، وطلب أوامر الصرف", type: "bool" },
                  { key: "supply_contracts_can_approve", label: "تعميد أو رفض أوامر الصرف وتأكيد الصرف", type: "bool" },
                ]} />
              <ModulePermsSection title="📤 أوامر صرف مستحقات المقاولين" color="#7c3aed" mainKey="can_view_contract_claims"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_contract_claims", label: "عرض الصفحة", type: "bool" },
                ]} />
              {/* v3.4.027 — توسعة صلاحيات مستخلص الاستلام: عرض / تقديم / تنفيذ / حذف */}
              <ModulePermsSection title="📥 رفع مستخلص استلام" color="#0369a1" mainKey="can_view_receipt_requests"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_receipt_requests", label: "عرض الصفحة وقائمة الطلبات", type: "bool" },
                  { key: "receipt_can_submit", label: "تقديم طلب مستخلص (الفورم + رفع الطلب)", type: "bool" },
                  { key: "receipt_can_execute", label: "تنفيذ الطلبات (زر «تم التنفيذ»)", type: "bool" },
                  { key: "receipt_can_delete", label: "حذف أي طلب (المنشئ يحذف طلبه الجديد دائمًا)", type: "bool" },
                ]} />
              {/* v3.4.026 — فصل الصلاحيات: تاب الخرائط المرجعية مستقل عن تاب هيكل العقود + علم الرفع */}
              <ModulePermsSection title="🗺 مرجع الخرائط — الخرائط المرجعية" color="#b91c1c" mainKey="can_view_drawings"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_drawings", label: "عرض تاب الخرائط المرجعية (قائمة المستندات)", type: "bool" },
                  { key: "drawings_can_upload", label: "رفع وتحديث الخرائط (PDF / كاد)", type: "bool" },
                ]} />
              <ModulePermsSection title="🏗 مرجع الخرائط — هيكل العقود" color="#9333ea" mainKey="can_view_contract_structure"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_contract_structure", label: "عرض وتعديل هيكل العقود (المباني/الأدوار/الأمتار) — العقد القائم يعدّله الأدمن فقط", type: "bool" }, // v3.4.029 — العلم يفتح التعديل أيضًا (كان الخادم يشترط صلاحيات المقاول الرئيسي)
                ]} />

              </GroupBox>

              <GroupBox gkey="screen" profile={editingProfile}>{/* v3.4.496 — مجموعة «الشاشة التفاعلية» كما في القائمة الجانبية */}
              <ModulePermsSection title="🖥️ الشاشة التفاعلية الموحّدة" color="#0f766e" mainKey="can_view_screen"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_screen", label: "عرض: رابط الشاشة + صفحة «الشاشة التفاعلية» (رابط الشاشة والمشاريع وتاب كل مشروع)", type: "bool" },
                  { key: "screen_can_manage", label: "إدارة: إظهار المشاريع على الشاشة وترتيبها، بطاقة كل مشروع، ورفع/إخفاء/حذف ملفاتها", type: "bool" }, /* v3.4.492 */
                ]} />
              </GroupBox>


              <GroupBox gkey="finance" profile={editingProfile}>
              {/* v3.3.308: قسم التحليل المالي — v3.3.431/432: completed list — v3.3.577: added treasury */}
              <ModulePermsSection title="📊 قسم التحليل المالي" color="#dc2626" mainKey="can_view_analytics"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_analytics", label: "الإحصائيات", type: "bool" },
                  { key: "can_view_financial_reports", label: "التقارير المالية", type: "bool" },
                  { key: "can_view_commissions", label: "الدلالات والعمولات", type: "bool" },
                  { key: "can_view_cash_flow", label: "تفاصيل الإيرادات", type: "bool" },
                  { key: "can_view_cash_flow_statement", label: "التدفقات النقدية", type: "bool" },
                  { key: "can_view_treasury", label: "الخزينة المالية", type: "bool" },
                  { key: "can_export_treasury", label: "تصدير الخزينة (Excel)", type: "bool" }, // v3.3.865
                  { key: "can_view_payroll", label: "عرض إدارة الرواتب", type: "bool" }, // v3.4.837
                  { key: "payroll_can_manage", label: "تعديل الرواتب والبدلات والسلف", type: "bool" }, // v3.4.837
                  { key: "can_view_benchmarking", label: "مقارنة المشاريع", type: "bool" },
                  { key: "can_view_company_assets", label: "أصول الشركة", type: "bool" },
                ]} />

              {/* v3.3.691 — قسم الرسوم الإدارية للمشاريع (صفحة مستقلة) */}
              <ModulePermsSection title="🏠 قسم الرسوم الإدارية للمشاريع" color="#0891b2" mainKey="can_view_admin_fees"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_admin_fees", label: "عرض", type: "bool" },
                ]} />

              {/* v3.3.432 — قسم المصاريف الرأسمالية (CapEx) منفصل */}
              <ModulePermsSection title="💼 قسم المصاريف الرأسمالية (CapEx)" color="#1e40af" mainKey="can_view_capex"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_capex", label: "عرض المشاريع الرأسمالية", type: "bool" },
                  { key: "capex_can_add", label: "إضافة مشروع جديد", type: "bool" },
                  { key: "capex_can_edit", label: "تعديل المشاريع والبنود", type: "bool" },
                  { key: "capex_can_delete", label: "حذف المشاريع والبنود", type: "bool" },
                  { key: "capex_can_export", label: "تصدير ملفات Excel", type: "bool" },
                  { key: "capex_can_import", label: "استيراد ملفات Excel", type: "bool" },
                ]} />

              {/* v3.3.418: قسم التمويل (الصكوك) */}
              <ModulePermsSection title="💰 قسم التمويل (الصكوك)" color="#d97706" mainKey="can_view_sukuk"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_sukuk", label: "عرض صفحة التمويل", type: "bool" },
                  { key: "sukuk_can_add", label: "إضافة عروض/عقود", type: "bool" },
                  { key: "sukuk_can_edit", label: "تعديل العروض/العقود/الإصدارات", type: "bool" },
                  { key: "sukuk_can_delete", label: "حذف العروض/العقود", type: "bool" },
                  { key: "sukuk_can_export", label: "تصدير ملفات Excel", type: "bool" },
                  { key: "sukuk_can_import", label: "استيراد ملفات Excel", type: "bool" },
                  { key: "sukuk_can_activate", label: "تفعيل الصكوك (إصدارها فعلياً)", type: "bool" },
                ]} />

              {/* v3.3.490: قسم البرنامج المحاسبي — v3.3.497: simplified with explicit list of granted actions */}
              <ModulePermsSection title="📒 قسم البرنامج المحاسبي" color="#0e7490" mainKey="can_view_accounting"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_accounting", label: "تفعيل الوصول الكامل للموجول (صلاحيات الأدمن)", type: "bool" },
                ]}
                footer={
                  <div style={{ marginTop: 8, padding: "8px 12px", background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 6, fontSize: 11, color: "#0e7490", lineHeight: 1.7 }}>
                    <strong>عند التفعيل يحصل المستخدم على كل صلاحيات الأدمن في الموجول المحاسبي:</strong>
                    <div style={{ marginTop: 5, color: "#0f172a", fontSize: 10.5 }}>
                      ✓ عرض المعاملات والتفاصيل ·
                      ✓ عرض مراكز التكلفة ودليل الحسابات ·
                      ✓ إنشاء وتعديل وحذف فروع شجرة مراكز التكلفة ·
                      ✓ إضافة جذور رئيسية للشجرة ·
                      ✓ ربط مراكز التكلفة بالفروع (سحب وإفلات / تصنيف) ·
                      ✓ إخفاء وإعادة إظهار مراكز التكلفة ·
                      ✓ عرض التقرير المُجمَّع ·
                      ✓ تشغيل المزامنة مع Daftra ·
                      ✓ تعديل إعدادات الـ API
                    </div>
                  </div>
                }
              />

              </GroupBox>

              <GroupBox gkey="system" profile={editingProfile}>
              {/* الإعدادات */}
              <ModulePermsSection title="⚙️ قسم النظام" color="#475569" mainKey="can_view_settings"
                profile={editingProfile} setProfile={setEditingProfile}
                fields={[
                  { key: "can_view_settings", label: "عرض وتعديل الإعدادات", type: "bool" },
                  { key: "can_view_audit", label: "صفحة التنبيهات (Audit Log)", type: "bool" },
                  // v3.3.865 — علم منفصل يفحصه API السجل — بدونه الصفحة تظهر لكن السجل يرفض التحميل
                  { key: "can_view_audit_log", label: "قراءة سجل التنبيهات (API)", type: "bool" },
                  // v3.4.243 — تاب إدارة المستخدمين (كان بلا مفتاح هنا) + زر المعاينة كصلاحية مستقلة
                  { key: "can_manage_users", label: "👥 تاب إدارة المستخدمين والبروفايلات", type: "bool" },
                  { key: "users_can_preview", label: "👁 زر «معاينة كمستخدم» (وضع المعاينة)", type: "bool" },
                  { key: "can_view_cloud_billing", label: "☁️ تاب «فواتير الكلاود» (DigitalOcean + Anthropic)", type: "bool" }, // v3.4.255
                  { key: "can_view_wa_updates_group", label: "📢 إدارة جروب التحديثات (واتس) — الاستراتيجية والسجل الأسبوعي", type: "bool" }, // v3.4.271
                  { key: "wa_updates_can_edit_strategy", label: "✏️ تعديل الاستراتيجية في إدارة جروب التحديثات", type: "bool" }, // v3.4.273
                ]} />

              </GroupBox>
              {/* v3.3.309: تم دمج Audit في قسم النظام أعلاه */}
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setEditingProfile(null)} style={{
                padding: "8px 20px", background: "#f1f5f9", border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: 13,
              }}>إلغاء</button>
              <button onClick={handleUpdateProfile} style={{
                padding: "8px 24px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600,
              }}>حفظ</button>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteUser && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowInviteUser(false)}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: 400, maxWidth: "90vw" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>إضافة مستخدم جديد</h3>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>البريد الإلكتروني</label>
              <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                placeholder="user@rael.com.sa"
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 13, marginTop: 4 }} />
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>البروفايل (اختياري)</label>
              <select value={inviteProfileId || ""} onChange={e => setInviteProfileId(e.target.value || null)}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 13, marginTop: 4 }}>
                <option value="">— بدون بروفايل —</option>
                {profilesList.map(p => <option key={p.id} value={p.id}>{p.name_ar}</option>)}
              </select>
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowInviteUser(false)} style={{
                padding: "8px 20px", background: "#f1f5f9", border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: 13,
              }}>إلغاء</button>
              <button onClick={handleInviteUser} style={{
                padding: "8px 24px", background: "#10b981", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600,
              }}>إضافة</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
